import React, { createContext, useEffect, useRef, useState, useContext } from "react";
import { Plan } from "src/domain/plan";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  addMessage,
  addRequestIds,
  getAskThreadMessages,
  setAskToDraftIntentWSResponse,
  setAwaitingResponse,
  setChangingDraftType,
  setChatInputMessage,
  setFailedToCreateThread,
  setDuplicateThreadError,
  setIsWsReconnected,
  setMessagingDisabled,
  setLastTriggeredMessageId,
  setRegenerationHappening,
  setTriggerRetryExhausted,
  setRetryWS,
  setRetryWSState,
  setScroll,
  setShowAskToDraftModal,
  setShowConnectionErrorModal,
  setUserInteractionAttempted,
  setupNewThread,
  updateLoadingMessageType,
  updateThreadMessages,
  updateThreadDataTitle,
  setFreeUserLimit,
  // New action creators for enhanced WebSocket error handling
  setThreadLimitReached,
  setAllowRegeneration,
  setLastErrorMessage,
  setConnectionStatus,
  setGPTContextError,
  handleGPTContextMessages,
  // Document unlock actions
  setActiveDocument,
  setUnlockingDocument,
  setGenerateDocsFetchRequiredCount,
  setShowThreadLimitModal,
  addQuickActionButtonsToMessage,
  // Message reliability actions
  updatePendingThreadStatus,
  clearPendingThreadCreation,
  addUnsentMessage,
  removeUnsentMessage,
  setRecoveryInProgress,
  updateUnsentMessageStatus,
  setShouldTriggerAIResponse,
  setIsNewThreadCreted,
  setAwaitingThreadCreationResponse,
  setButtonClickTypeForTrigger,
  setWsErrorCount,
  setWsConnectionErrorCount,
  incrementReconnectionAttemptCount,
  resetReconnectionAttemptCount,
  finalizeStreamingMessage,
  addDocumentPayment,
  setSignupAlertPopup,
  setShowDocumentLimitPopup,
  setAttachmentInfo,
} from "src/store/slices/messageSlice";

import { useAuth } from "@clerk/clerk-react";
import { insertNewThread, updateThreadTitleLocal, setThreadPrivileged } from "src/store/slices/threadsSlice";
import { setPaymentLinkExpired, setPaymentReceived } from "src/store/slices/chatSlice";
import { openWithEntry as openCheckoutWithEntry } from "src/store/slices/checkoutFlowSlice";
import { decreaseThreadsRemaining, getUserMetadata } from "src/store/slices/onboardingSlice";
import { applyRemapSuccess } from "src/utils/threadOwnershipGate";
import { resolveDesiredIdentity } from "src/utils/connectionIdentity";
import applicationStore from "./store/RootStore";

import { toast } from "react-toastify";
import { ErrorLoggingV2 } from "./utils/errorHelpers";
import { recordStreamFrame, isStreamActive } from "./utils/streamingActivity";
import { isActivityMessage, TOOL_NAMES } from "./utils/activitySignals";
import { MESSAGE_TYPES } from "./domain/messages/messageTypes";
import { recordResponseMilestones } from "./utils/responseMilestoneTelemetry";
import {
  updateTransactionStatus,
  PersistentStorage,
  checkForPendingOperations,
  MessageRecovery,
  fetchThreadStatus,
  pollThreadStatus,
} from "./utils/messageReliability";
import { clearLocalStorageKeys, isThreadPage, trackAnalytics } from "./pages/helpers";
import { rememberAnonymousThreadId } from "./utils/anonymousThreadId";
import { trackPixelEvent } from "./services/facebookPixel";
import { getInhouseAnonymousId, regenerateInhouseAnonymousId, setInhouseAnonymousIdCookie } from "./utils/anonymousId";
import { resetAnalyticsAnonymousId } from "./services/analyticsInit";
import { setUnacknowledgedMessages } from "src/store/slices/messageSlice";
import { updateVersionFromWS } from "src/store/slices/profileMemorySlice";
import { BANNER_TYPES, BUTTON_ROLES, EVENT_NAMES, EVENT_PARAMETERS, getPageName } from "./utils/EventConstants";
import { trackDocumentEditedByUser } from "./utils/documentAnalytics";
import { buildRegeneratePayload } from "./utils/regenerateHelpers";
import { isThreadInTerminalState } from "./utils/threadTerminalState";
import {
  areEmailThreadsTerminal,
  collectEmailAttachmentThreadIds,
  findPendingEmailAttachmentThreadIds,
} from "./utils/emailAttachmentTriggerGate";
import { getTriggerGeoFields } from "./utils/timezone";

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const dispatch = useDispatch();
  const url = process.env.REACT_APP_WS_BASE_ENDPOINT;
  var usermetadata = useSelector((state) => state.onboarding.userMetadata);
  // const [isConnected, setIsConnected] = useState(false);
  const [accesstoken, setAccesstoken] = useState(null);

  const { getToken, isLoaded, isSignedIn } = useAuth();
  const socketRef = useRef(null);
  const isConnectedRef = useRef(false);

  // WS identity reconciliation (ws-identity-reconciliation): the identity the
  // live socket was created with, mirrored into state so consumers
  // (useChatPageHook's gpt create-thread gate) re-render when it changes.
  const socketIdentityRef = useRef("none");
  const [connectionIdentity, setConnectionIdentity] = useState("none");
  // connectWebSocket is invoked from timeouts and stale closures (retry paths),
  // so it reads Clerk state through a ref that's refreshed every render.
  const clerkStateRef = useRef({ isLoaded: false, isSignedIn: false });
  clerkStateRef.current = { isLoaded, isSignedIn };
  // The provider mounts OUTSIDE the Router (App.js), so useLocation is
  // unavailable here. WebSocketLocationBridge (mounted inside the Router)
  // pushes pathname changes into this state to re-trigger reconciliation.
  const [currentPathname, setCurrentPathname] = useState(window.location.pathname);
  const identitySwapTimerRef = useRef(null);
  const identitySwapDeadlineRef = useRef(null);
  // Thread IDs created over an anonymous socket this session — remapped to the
  // user via getUserMetadata({ anonymousThreadId }) once the connection
  // becomes authenticated.
  const anonThreadIdsRef = useRef(new Set());
  const retryIntervalRef = useRef(null);
  // const [retryCount, setRetryCount] = useState(0);
  const retryCountRef = useRef(0);
  const lastPongReceivedRef = useRef(Date.now());
  const lastConnectedTimeRef = useRef(null);
  const connectionCheckIntervalRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const wasDisconnectedRef = useRef(false);
  const listMessagesLastFetchedRef = useRef(Date.now());

  // Add connection state management to prevent multiple connections
  const connectionStateRef = useRef("disconnected"); // 'disconnected', 'connecting', 'connected'
  const connectionMutexRef = useRef(false); // Prevents concurrent connection attempts
  const recoveryWindowEndRef = useRef(null); // Track when recovery window ends

  const MAX_RETRIES = 10;
  const CONNECTION_CHECK_INTERVAL = 10000; // Check connection every 10 seconds
  const PING_INTERVAL = 15000; // Send ping every 15 seconds
  const CONNECTION_TIMEOUT = PING_INTERVAL * (3 + 1); // Consider connection dead after 60 seconds (15seconds ping time * 3 + 15 seconds extra time taken to ping) of inactivity
  const LIST_MESSAGES_FETCH_INTERVAL = 30000; // Fetch list messages every 30 seconds if the websocket is not working
  const RECOVERY_WINDOW_MS = 5000; // 5 second recovery window after reconnection
  const THREAD_RECOVERY_EXPIRY_MS = 2 * 60 * 1000; // 2 minutes - don't recover threads older than this
  const IDENTITY_SWAP_RECHECK_MS = 2000; // Re-check for stream-idle before an identity swap
  const IDENTITY_SWAP_MAX_WAIT_MS = 30000; // Hard cap: swap even mid-stream after this long

  // Track last 5 connection errors for invalid token detection
  // When 3 consecutive invalid token errors occur,
  // we clear segment IDs from localStorage and regenerate them
  const lastConnectionErrorsRef = useRef([]);

  // Track auth error retries separately to prevent endless loops
  const authErrorRetryCountRef = useRef(0);
  const MAX_AUTH_RETRIES = 5; // Max retries specifically for auth errors before showing error modal

  // Track segment ID regeneration attempts
  const segmentIdRegenerationCountRef = useRef(0);
  const MAX_SEGMENT_ID_REGENERATIONS = 3; // Max times to regenerate segment ID

  // Timer to check if messaging gets enabled after document generation
  const messagingEnableTimeoutRef = useRef(null);
  const MESSAGING_ENABLE_TIMEOUT = 60000; // 60 seconds

  // Timer to detect document generation timeouts (no "document" message within 5s of generate_document loading message)
  const docGenTimeoutTimersRef = useRef({});
  const DOC_GEN_TIMEOUT_MS = 5000;

  // Track missed pongs to detect dead connections
  const missedPongsCountRef = useRef(0);
  const MAX_MISSED_PONGS = 3; // Show warning after 3 missed pongs (~45 seconds)

  // WS frame-ordering fix (email-attachment-contract-alignment): the backend's
  // chat_created and its follow-up message_added frame for a new thread aren't
  // guaranteed to be processed in that order. The existing message_added guard
  // (below) correctly drops frames for threads we're not currently viewing —
  // that protection stays. This buffer only catches the narrower case where a
  // message_added frame arrives for the thread we're actively in the middle of
  // creating, keyed by raw event.data so replay re-enters the exact same
  // receiveMessage pipeline instead of a hand-rolled partial re-dispatch.
  const pendingMessageAddedFramesRef = useRef({});
  const pendingThreadCreationStatusForBuffer = useSelector(
    (state) => state.messageSlice.pendingThreadCreation?.status,
  );
  useEffect(() => {
    if (pendingThreadCreationStatusForBuffer === "failed" || pendingThreadCreationStatusForBuffer == null) {
      pendingMessageAddedFramesRef.current = {};
    }
  }, [pendingThreadCreationStatusForBuffer]);

  // Retry trigger-ai-response when backend rejects with AttachmentsStillProcessing.
  // Backend doesn't notify when processing completes, so we re-ask every 30s.
  const attachmentRetryTimerRef = useRef(null);
  const attachmentRetryAttemptsRef = useRef(0);
  const attachmentRetryPayloadRef = useRef(null);
  const ATTACHMENT_RETRY_INTERVAL_MS = 30000;
  const ATTACHMENT_RETRY_MAX_ATTEMPTS = 20;

  // Email-context equivalent of the above, but held rather than retried on a
  // timer. The backend rejects trigger-ai-response with MessagingDisabledException
  // (not AttachmentsStillProcessing) while an attached email thread is still
  // extracting, so the 30s loop can't be reused — and there's no need for it:
  // useEmailAttachmentStatusPolling already reports readiness every 5s. Holds
  // {threadId, lastMessageId, buttonClickType, emailThreadIds} until then.
  const deferredEmailTriggerRef = useRef(null);
  const emailAttachmentStatusByThreadId = useSelector((state) => state.emailContext?.attachmentStatusByThreadId);
  const reconnectionAttemptInProgressRef = useRef(false); // Prevent multiple concurrent reconnection attempts
  const statusPollingIntervalRef = useRef(null);
  const STATUS_POLL_INTERVAL_MS = 15000; // Poll /status every 15s while awaiting AI response
  const streamingRecoveryPollRef = useRef(null);
  // The "is the WS still alive?" signal moved to a shared module
  // (`utils/streamingActivity`) — recovery poll AND the gate inside the
  // getAskThreadMessages thunk both consult the same lastFrameAt. Previously
  // this lived as a closure-local ref here; only the recovery poll knew
  // about it, and the ~20 other dispatch sites that call list-messages had
  // no equivalent gate. Live thread 12a5b88f-… reproduced the consequence:
  // three list-messages calls landed during a 10s streaming window despite
  // text_delta frames flowing the whole time, because the polls came from
  // dispatchers other than the recovery one.

  // Check if an error message indicates invalid token/auth issues
  const isInvalidTokenError = (errorMessage) => {
    if (!errorMessage || typeof errorMessage !== "string") return false;

    const invalidTokenPatterns = [
      "Failed to connect, invalid token!",
      "InvalidAuthTokenException",
      "signature has expired",
      "token has expired",
      "expired token",
      "invalid token",
      "unauthorized",
    ];

    return invalidTokenPatterns.some((pattern) => errorMessage.toLowerCase().includes(pattern.toLowerCase()));
  };

  // Handle auth token errors with auto-reconnect
  const handleAuthTokenError = async (_errorMessage) => {
    // 1. Show modal with reconnecting message
    dispatch(setLastErrorMessage("Connection issue detected. Reconnecting..."));
    dispatch(setShowConnectionErrorModal(true));

    // 2. Clear current token
    setAccesstoken(null);

    // 3. Get fresh token from Clerk
    try {
      const newToken = await getToken({ template: "Backend" });
      if (newToken) {
        setAccesstoken(newToken);
      }
    } catch (e) {
      ErrorLoggingV2(
        {
          error_type: "Token refresh failed",
          error_message: e.message,
          path: window.location.href,
        },
        "token_refresh_error",
      );
    }

    // 4. Close socket to trigger reconnect with new token
    if (socketRef.current) {
      socketRef.current.close();
    }
  };

  // Start timer to check if messaging gets enabled after document generation
  // If not enabled within 60 seconds, refetch list-messages to reset state
  const startMessagingEnableTimer = (threadId) => {
    // Clear any existing timer
    if (messagingEnableTimeoutRef.current) {
      clearTimeout(messagingEnableTimeoutRef.current);
    }

    messagingEnableTimeoutRef.current = setTimeout(() => {
      const currentState = applicationStore.getState();
      const isMessagingDisabled = currentState.messageSlice.messagingDisabled;
      const currentThreadId = currentState.messageSlice.threadData?.id;

      // If messaging is still disabled and we're on the same thread, refetch
      if (isMessagingDisabled && currentThreadId === threadId) {
        console.warn("Messaging not enabled after 60s. Refetching list-messages...");
        dispatch(getAskThreadMessages({ threadId, dispatchSource: "messagingEnableTimeout" }));
      }
      messagingEnableTimeoutRef.current = null;
    }, MESSAGING_ENABLE_TIMEOUT);
  };

  const startDocGenTimer = (triggerId, threadId) => {
    // Clear any existing timer for this trigger
    clearDocGenTimer(triggerId);

    docGenTimeoutTimersRef.current[triggerId] = setTimeout(() => {
      const userMetadata = applicationStore.getState().onboarding.userMetadata;
      trackAnalytics(
        EVENT_NAMES.DOCUMENT_GENERATION_FAILED,
        {
          [EVENT_PARAMETERS.THREAD_ID]: threadId,
          [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
          [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
          [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
          trigger_id: triggerId,
        },
        usermetadata,
      );
      delete docGenTimeoutTimersRef.current[triggerId];
    }, DOC_GEN_TIMEOUT_MS);
  };

  const clearDocGenTimer = (triggerId) => {
    if (docGenTimeoutTimersRef.current[triggerId]) {
      clearTimeout(docGenTimeoutTimersRef.current[triggerId]);
      delete docGenTimeoutTimersRef.current[triggerId];
    }
  };

  const stopStatusPolling = () => {
    if (statusPollingIntervalRef.current) {
      clearInterval(statusPollingIntervalRef.current);
      statusPollingIntervalRef.current = null;
    }
  };

  const startStatusPolling = (threadId) => {
    stopStatusPolling();

    statusPollingIntervalRef.current = setInterval(async () => {
      const currentState = applicationStore.getState();
      const isMessagingDisabled = currentState.messageSlice.messagingDisabled;
      const currentThreadId = currentState.messageSlice.threadData?.id;

      // Stop if messaging was already re-enabled (enable_messaging WS received) or thread changed
      if (!isMessagingDisabled || currentThreadId !== threadId) {
        stopStatusPolling();
        return;
      }

      const token = accesstoken || localStorage.getItem("accessToken2");
      const status = await fetchThreadStatus(threadId, token);

      if (!status) return; // API unreachable, keep polling

      if (status.action === "REFRESH") {
        dispatch(getAskThreadMessages({ threadId, dispatchSource: "statusPoll-REFRESH" }));
        dispatch(setAwaitingResponse(false));
        dispatch(setMessagingDisabled(false));
        dispatch(setChatInputMessage(""));
        stopStatusPolling();
      } else if (status.action === "ERROR") {
        dispatch(setAwaitingResponse(false));
        dispatch(setMessagingDisabled(false));
        toast.error("Something went wrong processing your message. Please try again.");
        stopStatusPolling();
      }
      // WAIT or RECONNECT: keep polling
    }, STATUS_POLL_INTERVAL_MS);
  };

  const stopStreamingRecoveryPoll = () => {
    if (streamingRecoveryPollRef.current) {
      clearInterval(streamingRecoveryPollRef.current);
      streamingRecoveryPollRef.current = null;
    }
  };

  const startStreamingRecoveryPoll = (threadId) => {
    if (streamingRecoveryPollRef.current) return;
    streamingRecoveryPollRef.current = setInterval(async () => {
      // Watchdog: if a text_delta arrived recently the WS is healthy and
      // will deliver the EnableMessaging frame itself. Reconciling via
      // REST mid-stream races the live deltas — getAskThreadMessages
      // wholesale-replaces state.threadData.messages, which wipes the
      // in-flight typewriter text if the API call lands before the
      // backend has persisted the assistant turn (V3.0 only persists at
      // USER_REPLY_TEXT, end of stream). Only step in when the WS has
      // gone quiet long enough to look genuinely dropped.
      if (isStreamActive()) {
        return;
      }
      try {
        let headers;
        if (window.location.pathname.includes("/try")) {
          const anonymousId = await getInhouseAnonymousId();
          headers = { "anonymous-user-id": anonymousId };
        } else {
          const token = accesstoken || localStorage.getItem("accessToken2");
          headers = { Authorization: `Bearer ${token}` };
        }
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_BASE_ENDPOINT}/api/thread/${threadId}/list-messages?source=recovery-poll`,
          { headers },
        );
        const data = await res.json();
        if (data.messaging_disabled === false) {
          stopStreamingRecoveryPoll();
          dispatch(getAskThreadMessages({ threadId, dispatchSource: "recovery-poll-success" }));
          dispatch(setMessagingDisabled(false));
          dispatch(setAwaitingResponse(false));
        }
      } catch (_) { }
    }, 3000);
  };

  const randomString = (length) => {
    const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  };

  // Cancel any in-flight AttachmentsStillProcessing retry. Called when streaming starts,
  // a new user message is sent, or the user navigates away.
  const cancelAttachmentRetry = () => {
    if (attachmentRetryTimerRef.current) {
      clearTimeout(attachmentRetryTimerRef.current);
      attachmentRetryTimerRef.current = null;
    }
    attachmentRetryAttemptsRef.current = 0;
    attachmentRetryPayloadRef.current = null;
    // A held email-context trigger belongs to the turn being abandoned too —
    // every caller (new add-message, streaming started, thread switched) means
    // it must not fire later. Safe against the add-message case specifically:
    // that call happens on the outbound send, before the message_added frame
    // that sets the deferral comes back.
    deferredEmailTriggerRef.current = null;
  };

  // Schedule the next retry of trigger-ai-response. Backend rejected with
  // AttachmentsStillProcessing; ask again in 30s.
  const scheduleAttachmentRetry = (threadId, lastMessageId, buttonClickType) => {
    if (attachmentRetryTimerRef.current) {
      clearTimeout(attachmentRetryTimerRef.current);
      attachmentRetryTimerRef.current = null;
    }
    if (attachmentRetryAttemptsRef.current >= ATTACHMENT_RETRY_MAX_ATTEMPTS) {
      dispatch(setTriggerRetryExhausted(true));
      attachmentRetryPayloadRef.current = null;
      return;
    }
    attachmentRetryPayloadRef.current = { threadId, lastMessageId, buttonClickType };
    attachmentRetryTimerRef.current = setTimeout(() => {
      attachmentRetryTimerRef.current = null;
      const activeThreadId = applicationStore.getState().messageSlice.threadData?.id;
      if (activeThreadId !== threadId) return;
      if (!isConnectedRef.current) return;
      const triggerAIMessage = createMessage("ask", "trigger-ai-response", {
        thread_id: threadId,
        last_message_id: lastMessageId,
        button_click_type: buttonClickType || null,
      });
      sendMessage(triggerAIMessage, true);
      attachmentRetryAttemptsRef.current += 1;
    }, ATTACHMENT_RETRY_INTERVAL_MS);
  };

  // Clean up existing WebSocket connection and intervals
  const cleanupExistingConnection = () => {
    // Clear intervals
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (connectionCheckIntervalRef.current) {
      clearInterval(connectionCheckIntervalRef.current);
      connectionCheckIntervalRef.current = null;
    }
    if (retryIntervalRef.current) {
      clearTimeout(retryIntervalRef.current);
      retryIntervalRef.current = null;
    }

    // Clear messaging enable timer
    if (messagingEnableTimeoutRef.current) {
      clearTimeout(messagingEnableTimeoutRef.current);
      messagingEnableTimeoutRef.current = null;
    }

    // Clear status polling
    stopStatusPolling();

    // Close existing socket
    if (socketRef.current) {
      // Remove event listeners to prevent them from firing during cleanup
      socketRef.current.onopen = null;
      socketRef.current.onclose = null;
      socketRef.current.onmessage = null;
      socketRef.current.onerror = null;

      if (socketRef.current.readyState === WebSocket.OPEN || socketRef.current.readyState === WebSocket.CONNECTING) {
        socketRef.current.close();
      }
      socketRef.current = null;
    }

    // Reset connection state
    isConnectedRef.current = false;
    connectionStateRef.current = "disconnected";
    socketIdentityRef.current = "none";
    setConnectionIdentity("none");
  };

  // Execute (or defer) a pending identity swap. Re-resolves the desired
  // identity at fire time — the user may have navigated or signed out since
  // the swap was scheduled.
  const executeIdentitySwap = () => {
    identitySwapTimerRef.current = null;
    const desired = resolveDesiredIdentity({
      pathname: window.location.pathname,
      isClerkLoaded: clerkStateRef.current.isLoaded,
      isSignedIn: clerkStateRef.current.isSignedIn,
    });
    if (desired === "none" || socketIdentityRef.current === desired || connectionStateRef.current !== "connected") {
      identitySwapDeadlineRef.current = null;
      return;
    }
    // Stream-safe deferral: don't clip an in-flight AI response. After the
    // hard cap, swap anyway — a wrong-identity socket is worse than a
    // clipped stream.
    if (isStreamActive() && Date.now() < identitySwapDeadlineRef.current) {
      identitySwapTimerRef.current = setTimeout(executeIdentitySwap, IDENTITY_SWAP_RECHECK_MS);
      return;
    }
    const fromIdentity = socketIdentityRef.current;
    trackAnalytics(EVENT_NAMES.WS_IDENTITY_RECONCILED, {
      [EVENT_PARAMETERS.FROM_IDENTITY]: fromIdentity,
      [EVENT_PARAMETERS.TO_IDENTITY]: desired,
      [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
      [EVENT_PARAMETERS.THREAD_ID]: applicationStore.getState().messageSlice.threadData?.id || null,
    });
    identitySwapDeadlineRef.current = null;
    // cleanupExistingConnection detaches the socket's handlers before closing,
    // so this deliberate teardown never enters the onclose retry/auth-error
    // machinery.
    cleanupExistingConnection();
    connectWebSocket();
  };

  const scheduleIdentitySwap = () => {
    if (identitySwapTimerRef.current) {
      return; // A swap attempt is already pending
    }
    if (identitySwapDeadlineRef.current == null) {
      identitySwapDeadlineRef.current = Date.now() + IDENTITY_SWAP_MAX_WAIT_MS;
    }
    executeIdentitySwap();
  };

  // Remap threads created over an anonymous socket to the now-authenticated
  // user via the backend's anonymous_thread_id reassignment on get-metadata.
  // Each ID is attempted exactly once (dropped up-front — no retry loop).
  const recoverAnonymousThreads = () => {
    const threadIds = Array.from(anonThreadIdsRef.current);
    anonThreadIdsRef.current.clear();
    threadIds.forEach((threadId) => {
      dispatch(getUserMetadata({ anonymousThreadId: threadId })).then((result) => {
        if (result?.meta?.requestStatus === "fulfilled") {
          trackAnalytics(EVENT_NAMES.RECOVERED_ANONYMOUS_THREAD, {
            [EVENT_PARAMETERS.THREAD_ID]: threadId,
            [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
          });
          // Shared with the ownership retry hook — see threadOwnershipGate.
          applyRemapSuccess({ threadId, dispatch, getState: applicationStore.getState });
        } else {
          ErrorLoggingV2(
            {
              error_type: "Anonymous thread remap failed",
              error_message: `getUserMetadata with anonymous_thread_id rejected for thread ${threadId}`,
              thread_id: threadId,
              path: window.location.href,
            },
            "anonymous_thread_remap_failed",
          );
        }
      });
    });
  };

  // Identity-driven connection management. Replaces the old unconditional
  // mount-time connect + token/metadata effect: connect only once the desired
  // identity is resolvable ("none" while Clerk loads — connecting earlier is
  // what created anonymous-owned threads for signed-in users), and reconcile
  // a live socket whose identity no longer matches the auth context (e.g. the
  // anonymous /try socket surviving the redirect to /chat).
  useEffect(() => {
    const desired = resolveDesiredIdentity({
      pathname: currentPathname,
      isClerkLoaded: isLoaded,
      isSignedIn: isSignedIn,
    });
    if (desired === "none") {
      return;
    }
    if (connectionStateRef.current === "disconnected") {
      connectWebSocket();
    } else if (connectionStateRef.current === "connected" && socketIdentityRef.current !== desired) {
      scheduleIdentitySwap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, isSignedIn, accesstoken, usermetadata, currentPathname]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (identitySwapTimerRef.current) {
        clearTimeout(identitySwapTimerRef.current);
        identitySwapTimerRef.current = null;
      }
      cleanupExistingConnection();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getTokenValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getToken]);

  useEffect(() => {
    const handleTabClose = () => {
      cleanupExistingConnection();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Reset last pong time to prevent immediate timeout detection
        lastPongReceivedRef.current = Date.now();

        // Close any existing connection error modal when page becomes visible
        const showConnectionErrorModal = applicationStore.getState().messageSlice.showConnectionErrorModal;
        if (showConnectionErrorModal) {
          dispatch(setShowConnectionErrorModal(false));
          dispatch(setUserInteractionAttempted(false));
        }

        // Check if we need to reconnect using our new state management
        if (connectionStateRef.current === "disconnected") {
          connectWebSocket();
        } else if (connectionStateRef.current === "connected") {
          // Send a ping to check if connection is still alive
          const pingMessage = {
            action: "ping",
            payload: null, // Update to new format
            trigger_ws_message_id: randomString(10),
          };
          try {
            socketRef.current.send(JSON.stringify(pingMessage));
          } catch (e) {
            console.warn("Failed to send ping on visibility change", e);
            // Connection might be broken, mark as disconnected
            connectionStateRef.current = "disconnected";
            connectWebSocket();
          }
        }
      }
    };

    const handleFocus = () => {
      // // console.log("Window focused, checking WebSocket connection...");
      handleVisibilityChange(); // Reuse the same logic
    };

    window.addEventListener("beforeunload", handleTabClose);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("beforeunload", handleTabClose);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getMyToken = async () => {
    const token = await getToken({ template: "Backend" });
    return token;
  };
  const getTokenValue = async () => {
    const token = await getMyToken();
    if (token && typeof token === "string") {
      setAccesstoken(token);
    }
  };

  const handleSocketClose = (dispatch, retryIntervalRef, isConnectedRef, retryCountRef, socketRef) => {
    dispatch(setRetryWSState("idle"));
    dispatch(setRetryWS(true));
    isConnectedRef.current = false;

    // Stop retrying if auth errors have exceeded the limit
    if (authErrorRetryCountRef.current >= MAX_AUTH_RETRIES) {
      console.warn("Auth error retry limit exceeded. Stopping reconnection attempts.");
      dispatch(setLastErrorMessage("Authentication failed. Please refresh the page or log in again."));
      dispatch(setShowConnectionErrorModal(true));
      dispatch(setConnectionStatus("error"));
      return;
    }

    if (retryCountRef.current >= MAX_RETRIES) {
      console.warn("Max retries exceeded. Showing connection error modal.");
      dispatch(setLastErrorMessage("Unable to connect. Please check your connection and try again."));
      dispatch(setShowConnectionErrorModal(true));
      return;
    }

    const retryDelay = 3000;

    retryIntervalRef.current = setTimeout(() => {
      if (
        !socketRef.current ||
        socketRef.current.readyState === WebSocket.CLOSED ||
        socketRef.current.readyState === WebSocket.CLOSING
      ) {
        retryCountRef.current += 1;
        connectWebSocket();
      }
    }, retryDelay);
  };

  const connectWebSocket = async () => {
    // Prevent multiple concurrent connection attempts
    if (connectionMutexRef.current) {
      // console.log("Connection attempt already in progress, skipping...");
      // console.log("Connection attempt already in progress, skipping...");
      return;
    }

    // Check if we're already connected or connecting
    if (connectionStateRef.current === "connected" || connectionStateRef.current === "connecting") {
      // console.log(`WebSocket already ${connectionStateRef.current}, skipping connection attempt`);
      // console.log(`WebSocket already ${connectionStateRef.current}, skipping connection attempt`);
      return;
    }

    // Check retry limit
    if (retryCountRef.current >= MAX_RETRIES) {
      const userInteractionAttempted = applicationStore.getState().messageSlice.userInteractionAttempted;
      if (userInteractionAttempted) {
        dispatch(setShowConnectionErrorModal(true)); // 👈 Trigger modal only if user tried to interact
      }
      ErrorLoggingV2(
        {
          error_type: `WebSocket retry limit exceeded: connectWebSocket`,
          error_message: `Retried ${retryCountRef.current} times, giving up. `,
          path: window.location.href,
        },
        "websocket_retry_exceeded",
      );
      cleanupExistingConnection();
      return;
    }

    // Set mutex to prevent concurrent attempts
    connectionMutexRef.current = true;
    connectionStateRef.current = "connecting";

    try {
      // Clean up any existing connection before creating new one
      cleanupExistingConnection();

      var socket;

      // Identity comes from auth context, not from the route alone. "none"
      // (Clerk still loading, or signed-out on an authed route) means no
      // socket at all — connecting anonymously before Clerk resolves is what
      // orphaned gpt threads under anonymous_user_id.
      const desiredIdentity = resolveDesiredIdentity({
        pathname: window.location.pathname,
        isClerkLoaded: clerkStateRef.current.isLoaded,
        isSignedIn: clerkStateRef.current.isSignedIn,
      });

      if (desiredIdentity === "none") {
        connectionMutexRef.current = false;
        connectionStateRef.current = "disconnected";
        return;
      }

      if (desiredIdentity === "authenticated") {
        if (!accesstoken) {
          connectionMutexRef.current = false;
          connectionStateRef.current = "disconnected";
          return;
        }
        if (!usermetadata?.subscription_type) {
          connectionMutexRef.current = false;
          connectionStateRef.current = "disconnected";
          return;
        }
        socket = new WebSocket(`${url}/ws?token=${accesstoken}`);
      } else {
        // If anonId is passed in query params (e.g. coming from another subdomain),
        // override the cookie so the WS connects with the same anonymous ID
        // that was used to upload attachments on the source platform.
        const urlAnonId = new URLSearchParams(window.location.search).get("anonId");
        if (urlAnonId) {
          setInhouseAnonymousIdCookie(urlAnonId);
          console.log("Set inhouse_anonymous_id cookie from URL param:", urlAnonId);
        }

        var id = await getInhouseAnonymousId();
        if (!id) {
          ErrorLoggingV2(
            { error_type: `Anonymous ID not found`, error_message: `Anonymous ID not found` },
            "anonymous_id_not_found",
          );
          connectionMutexRef.current = false;
          connectionStateRef.current = "disconnected";
          return;
        }
        socket = new WebSocket(`${url}/ws?anonymous_user_id=${id}`);
      }

      socketRef.current = socket;
      socketIdentityRef.current = desiredIdentity;
      setConnectionIdentity(desiredIdentity);

      // Set connecting state
      dispatch(setConnectionStatus("connecting"));

      socketRef.current.onopen = () => {
        // Update connection state
        isConnectedRef.current = true;
        connectionStateRef.current = "connected";
        connectionMutexRef.current = false; // Release mutex on successful connection
        // Anonymous ID is now stored in cookies (inhouse_anonymous_id), not localStorage

        lastConnectedTimeRef.current = Date.now();
        lastPongReceivedRef.current = Date.now();
        retryCountRef.current = 0;
        missedPongsCountRef.current = 0; // Reset missed pongs counter on successful connection
        reconnectionAttemptInProgressRef.current = false; // Reset reconnection flag
        // NOTE: Don't reset authErrorRetryCountRef or segmentIdRegenerationCountRef here!
        // They should only be reset when we receive "Connection successful!" message
        // because onopen fires even when connection will immediately fail with auth error
        dispatch(setRetryWSState("idle"));
        dispatch(setRetryWS(false));
        dispatch(setShowConnectionErrorModal(false));
        dispatch(setLastErrorMessage(null)); // Clear any auth error message on successful reconnection
        dispatch(setUserInteractionAttempted(false));
        dispatch(setConnectionStatus("connected"));
        dispatch(setWsConnectionErrorCount(0)); // Reset connection error count on successful connection
        dispatch(resetReconnectionAttemptCount()); // Reset reconnection attempt count on successful connection

        const isReconnecting = wasDisconnectedRef.current;
        if (wasDisconnectedRef.current) {
          dispatch(setIsWsReconnected(true));
          wasDisconnectedRef.current = false;
        }
        if (localStorage.getItem("userDisconnected")) {
          localStorage.removeItem("userDisconnected");
        }

        // Set recovery window if we're reconnecting
        if (wasDisconnectedRef.current) {
          recoveryWindowEndRef.current = Date.now() + RECOVERY_WINDOW_MS;
          // console.log("Setting recovery window for", RECOVERY_WINDOW_MS, "ms");
        }

        // Check for pending operations and recover
        const pendingOps = checkForPendingOperations();
        const currentState = applicationStore.getState();
        const pendingThreadCreation = currentState.messageSlice.pendingThreadCreation;
        const unsentMessages = currentState.messageSlice.messageReliability?.unsentMessages || {};

        // Recovery logic for pending thread creation
        if (
          pendingOps.hasPendingMessage ||
          pendingThreadCreation?.status === "failed" ||
          pendingThreadCreation?.status === "created"
        ) {
          // console.log("Recovering pending thread creation on reconnection", {
          //   pendingThreadStatus: pendingThreadCreation?.status,
          //   threadId: pendingThreadCreation?.threadId,
          // });
          // console.log("Recovering pending thread creation on reconnection", {
          //   pendingThreadStatus: pendingThreadCreation?.status,
          //   threadId: pendingThreadCreation?.threadId,
          // });
          dispatch(setRecoveryInProgress(true));

          const pendingMessage = pendingOps.pendingMessage || {
            message: pendingThreadCreation?.message,
            attachments: pendingThreadCreation?.attachments || [],
            transactionId: pendingThreadCreation?.transactionId,
            timestamp: pendingThreadCreation?.timestamp,
          };

          // Check if the pending message is too old (> 2 minutes)
          // This prevents confusing the user when they return much later
          const messageAge = pendingMessage.timestamp ? Date.now() - pendingMessage.timestamp : 0;
          if (messageAge > THREAD_RECOVERY_EXPIRY_MS) {
            PersistentStorage.clearPendingMessage();
            dispatch(clearPendingThreadCreation());
            dispatch(setRecoveryInProgress(false));
            return;
          }

          if (pendingMessage.message) {
            // CRITICAL FIX: Use threadId from localStorage if Redux state was reset (e.g., page refresh)
            const threadIdFromStorage = pendingMessage.threadId;
            const threadIdFromRedux = pendingThreadCreation?.threadId;
            const existingThreadId = threadIdFromRedux || threadIdFromStorage;

            // Determine if thread was already created
            const threadAlreadyCreated =
              (pendingThreadCreation?.status === "created" && existingThreadId) ||
              (threadIdFromStorage && !pendingThreadCreation?.status); // Redux reset but localStorage has threadId

            // Check if thread was already created but message wasn't sent
            if (threadAlreadyCreated && existingThreadId) {
              // Restore Redux state if it was reset but we have threadId from localStorage
              if (!pendingThreadCreation?.status && threadIdFromStorage) {
                dispatch(
                  updatePendingThreadStatus({
                    status: "created",
                    threadId: threadIdFromStorage,
                    message: pendingMessage.message,
                    attachments: pendingMessage.attachments || [],
                    transactionId: pendingMessage.transactionId,
                    buttonClickType: pendingMessage.buttonClickType,
                  }),
                );
              }

              // Check backend processing state before deciding what to do next.
              // This prevents re-sending a message the backend already received.
              (async () => {
                const token = accesstoken || localStorage.getItem("accessToken2");
                let threadStatus = await fetchThreadStatus(existingThreadId, token);

                // If still processing, poll until it resolves
                if (threadStatus?.action === "WAIT") {
                  threadStatus = await pollThreadStatus(existingThreadId, token);
                }

                if (threadStatus?.action === "REFRESH") {
                  // Backend already processed the message — just reload messages and clear state
                  dispatch(
                    getAskThreadMessages({
                      threadId: existingThreadId,
                      dispatchSource: "wsReconnect-recovery-REFRESH",
                    }),
                  );
                  dispatch(clearPendingThreadCreation());
                  PersistentStorage.clearPendingMessage();
                  dispatch(setRecoveryInProgress(false));
                  return;
                }

                if (threadStatus?.action === "ERROR") {
                  dispatch(clearPendingThreadCreation());
                  PersistentStorage.clearPendingMessage();
                  dispatch(setRecoveryInProgress(false));
                  toast.error("Something went wrong processing your message. Please try again.");
                  return;
                }

                // action === "RECONNECT", null (API unreachable), or status fetch failed —
                // fall back to sending the message over WS
                const messagePayload = {
                  thread_id: existingThreadId,
                  message: pendingMessage.message,
                };

                if (pendingMessage.attachments && pendingMessage.attachments.length > 0) {
                  messagePayload.reference_doc_storage_ids = pendingMessage.attachments;
                }

                if (pendingMessage.buttonClickType) {
                  messagePayload.button_click_type = pendingMessage.buttonClickType;
                }

                const wsMessage = createMessage("ask", "add-message", messagePayload);
                const success = sendMessage(wsMessage, true);

                if (success) {
                  updateTransactionStatus(pendingMessage.transactionId, "MESSAGE_SENDING", {
                    threadId: existingThreadId,
                  });
                }
              })();
            } else {
              // Retry thread creation from the beginning
              const recovery = MessageRecovery.recoverTransaction(pendingMessage.transactionId);
              if (recovery && recovery.action === "RETRY_THREAD_CREATION") {
                const wsMessage = createMessage("chat", "create-thread", {});
                const success = sendMessage(wsMessage, true);

                if (success) {
                  dispatch(
                    updatePendingThreadStatus({
                      status: "creating",
                    }),
                  );
                }
              }
            }
          }
        }

        // Recovery logic for unsent messages
        if (Object.keys(unsentMessages).length > 0) {
          // console.log("Recovering unsent messages on reconnection");

          Object.values(unsentMessages).forEach((unsentMessage) => {
            // Skip messages with no valid threadId — retrying them just
            // triggers backend InvalidWebsocketMessagePayloadException rejections.
            if (!unsentMessage.threadId) {
              dispatch(removeUnsentMessage(unsentMessage.id));
              return;
            }

            if (MessageRecovery.shouldRetry({ attempts: Array(unsentMessage.retryCount || 0) })) {
              const messagePayload = {
                thread_id: unsentMessage.threadId,
                message: unsentMessage.message,
              };

              if (unsentMessage.attachments && unsentMessage.attachments.length > 0) {
                messagePayload.reference_doc_storage_ids = unsentMessage.attachments;
              }

              if (unsentMessage.buttonClickType) {
                messagePayload.button_click_type = unsentMessage.buttonClickType;
              }

              const wsMessage = createMessage("ask", "add-message", messagePayload);
              const success = sendMessage(wsMessage, true);

              if (success) {
                dispatch(
                  updateUnsentMessageStatus({
                    id: unsentMessage.id,
                    status: "retrying",
                    retryCount: (unsentMessage.retryCount || 0) + 1,
                  }),
                );
              }
            }
          });
        }

        if (
          pendingOps.hasPendingMessage ||
          pendingOps.hasIncompleteTransactions ||
          Object.keys(unsentMessages).length > 0
        ) {
          setTimeout(() => {
            dispatch(setRecoveryInProgress(false));
          }, 2000); // Give some time for recovery operations
        }
        // Fetch latest messages if we're reconnecting and on a thread page
        if (isReconnecting && isThreadPage()) {
          const reconnectState = applicationStore.getState();
          const threadId = reconnectState.messageSlice.threadData?.id;
          if (threadId && window.location.pathname.toLowerCase().search("shared") === -1) {
            dispatch(getAskThreadMessages({ threadId, dispatchSource: "wsReconnect-threadPage" })).then((result) => {
              const data = result?.payload?.data;
              if (!data) return;
              const messages = data.messages || [];
              const lastMessage = messages[messages.length - 1];
              const messagingDisabled = data.messaging_disabled === true;
              // Recovery rule: trigger if last message is from the user, OR is an attachment and
              // messaging is still disabled (server signal that backend is waiting for AI).
              const needsTrigger =
                lastMessage?.is_user_message ||
                (lastMessage?.message_type === "attachment" && messagingDisabled);
              if (needsTrigger) {
                // trigger-ai-response may have been lost during WS drop — re-trigger.
                // setAwaitingResponse refreshes awaitingResponseStartTime so WSMonitor's 60s
                // retry timer measures from this attempt.
                dispatch(setAwaitingResponse(true));
                dispatch(setShouldTriggerAIResponse(true));
              }
            });
          }
        }

        // Restart status polling if AI was still processing before reconnect.
        // Skip if the thread is in a terminal state (e.g. message limit reached,
        // context exceeded) — polling can never resolve those, only user action can.
        // Exception: active regeneration where lastMessage is the regen marker but
        // the AI turn is genuinely in progress.
        const pollingState = applicationStore.getState();
        const pollingLastMsgType = pollingState.messageSlice.threadData?.lastMessage?.message_type;
        const isTerminalThread = isThreadInTerminalState(pollingLastMsgType) && !pollingState.messageSlice.regenerationHappening;
        const shouldRestartPolling =
          pollingState.messageSlice.messagingDisabled &&
          pollingState.messageSlice.threadData?.id &&
          !pollingState.messageSlice.pendingThreadCreation?.status &&
          !isTerminalThread;
        if (shouldRestartPolling) {
          startStatusPolling(pollingState.messageSlice.threadData.id);
        }

        // Check for pending redirect after reconnection
        const pendingRedirect = sessionStorage.getItem("pendingRedirect");
        if (pendingRedirect) {
          // console.log("Executing pending redirect after reconnection:", pendingRedirect);
          sessionStorage.removeItem("pendingRedirect");
          dispatch({
            type: "messageSlice/setRedirectTo",
            payload: pendingRedirect,
          });
        }

        // Start ping interval and connection monitoring when socket opens
        startPingInterval();
        setupConnectionCheck();
      };

      socketRef.current.onclose = (closeEvent) => {
        // Update connection state
        isConnectedRef.current = false;
        connectionStateRef.current = "disconnected";
        connectionMutexRef.current = false; // Release mutex on close
        socketIdentityRef.current = "none";
        setConnectionIdentity("none");

        dispatch(setIsWsReconnected(false));
        dispatch(setConnectionStatus("disconnected"));
        wasDisconnectedRef.current = true;

        // Clear ping interval on socket close
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        // Check if this is an auth-related close (check close code and reason)
        // Common auth-related close codes: 4001, 4003, 1008 (Policy Violation)
        // Also check the close reason for auth error patterns
        const isAuthClose =
          closeEvent?.code === 4001 ||
          closeEvent?.code === 4003 ||
          closeEvent?.code === 1008 ||
          isInvalidTokenError(closeEvent?.reason);

        if (isAuthClose || isInvalidTokenError(closeEvent?.reason)) {
          console.warn("WebSocket closed due to auth error:", closeEvent?.code, closeEvent?.reason);

          // Check if we've exceeded auth retry limit
          if (authErrorRetryCountRef.current >= MAX_AUTH_RETRIES) {
            console.warn("Max auth retries exceeded. Showing error modal.");
            dispatch(setLastErrorMessage("Authentication failed. Please refresh the page or log in again."));
            dispatch(setShowConnectionErrorModal(true));
            dispatch(setConnectionStatus("error"));
            return; // Stop retrying
          }

          // Increment auth retry counter and attempt token refresh
          authErrorRetryCountRef.current += 1;
          handleAuthTokenError(closeEvent?.reason || "Authentication error");
          return; // Don't proceed with normal retry logic
        }

        handleSocketClose(dispatch, retryIntervalRef, isConnectedRef, retryCountRef, socketRef);
      };

      socketRef.current.onerror = async (error) => {
        // Update connection state on error
        connectionStateRef.current = "disconnected";
        connectionMutexRef.current = false; // Release mutex on error
        dispatch(setConnectionStatus("error"));

        console.warn("WebSocket error occurred:", error);

        // Get current error count and increment
        const currentErrorCount = applicationStore.getState().messageSlice.wsConnectionErrorCount || 0;
        const newErrorCount = currentErrorCount + 1;
        dispatch(setWsConnectionErrorCount(newErrorCount));

        // If we're getting repeated errors quickly (likely auth issue), show modal after 5 errors
        if (newErrorCount >= 5 && authErrorRetryCountRef.current < MAX_AUTH_RETRIES) {
          // This might be an auth error that didn't provide a clear close reason
          // Attempt token refresh
          authErrorRetryCountRef.current += 1;
          console.warn(
            `Repeated connection errors (${newErrorCount}). Attempting token refresh (attempt ${authErrorRetryCountRef.current}/${MAX_AUTH_RETRIES})`,
          );

          dispatch(setLastErrorMessage("Connection issue detected. Reconnecting..."));
          dispatch(setShowConnectionErrorModal(true));
          dispatch(setConnectionStatus("connecting"));

          // Refresh token
          try {
            const newToken = await getToken({ template: "Backend" });
            if (newToken) {
              setAccesstoken(newToken);
            }
          } catch (e) {
            console.warn("Token refresh failed:", e);
          }
        }

        // Only log error if count is a multiple of 11
        if (newErrorCount % 11 === 0) {
          ErrorLoggingV2(
            {
              error_type: `WebSocket connection error`,
              error_message: `Error during connection: ${error.message || "Unknown error"
                } (Error count: ${newErrorCount})`,
              path: window.location.href,
              error_count: newErrorCount,
            },
            "websocket_connection_error",
          );
        }

        // If max auth retries exceeded or too many general errors, show final error
        if (authErrorRetryCountRef.current >= MAX_AUTH_RETRIES) {
          dispatch(setLastErrorMessage("Authentication failed. Please refresh the page or log in again."));
          dispatch(setShowConnectionErrorModal(true));
          dispatch(setConnectionStatus("error"));
        } else if (newErrorCount >= 15) {
          toast.error(
            "We're experiencing persistent connection issues. Please check your internet connection or try refreshing the page.",
          );
          console.warn(
            "We're experiencing persistent connection issues. Please check your internet connection or try refreshing the page.",
          );
          setTimeout(() => {
            window.location.reload();
          }, 5000);
        }
      };
      if (socketRef.current.readyState === WebSocket.CONNECTING) {
        setupConnectionCheck();
      }
    } catch (error) {
      // Handle any errors during connection creation
      connectionMutexRef.current = false;
      connectionStateRef.current = "disconnected";

      ErrorLoggingV2(
        {
          error_type: `WebSocket creation error`,
          error_message: `Error creating WebSocket: ${error.message}`,
          path: window.location.href,
        },
        "websocket_creation_error",
      );

      handleSocketClose(dispatch, retryIntervalRef, isConnectedRef, retryCountRef, socketRef);
    }

    const receiveMessage = async (event) => {
      var message = event.data;
      var requestIds = applicationStore.getState().messageSlice.requestIds;

      // Handle anonymous ID conflict before generic auth handling: regenerate ID and reconnect
      if (event.data.toLowerCase().includes("anonymousidbelongstosignedupuserexception")) {
        try {
          const newId = await regenerateInhouseAnonymousId();
          resetAnalyticsAnonymousId(newId);
          clearLocalStorageKeys();
        } catch (e) {
          ErrorLoggingV2(
            { error_type: `Failed to regenerate anonymous ID`, error_message: `${JSON.stringify(e)}` },
            "failed_to_regenerate_anonymous_id",
          );
        }
        if (socketRef.current) {
          socketRef.current.close();
        }
        return;
      }

      // Track connection errors for invalid token detection
      if (isInvalidTokenError(event.data)) {
        // Add to error history
        lastConnectionErrorsRef.current = [...lastConnectionErrorsRef.current, event.data];

        // Increment auth retry counter
        authErrorRetryCountRef.current += 1;
        console.warn(
          `Auth error received (attempt ${authErrorRetryCountRef.current}/${MAX_AUTH_RETRIES}):`,
          event.data,
          localStorage.getItem("ajs_user_id"),
        );

        // Check if we've exceeded max auth retries - if so, stop and show final error
        if (authErrorRetryCountRef.current >= MAX_AUTH_RETRIES) {
          console.warn("Max auth retries exceeded. Stopping reconnection attempts.");
          dispatch(setLastErrorMessage("Authentication failed. Please refresh the page or log in again."));
          dispatch(setShowConnectionErrorModal(true));
          dispatch(setConnectionStatus("error"));
          // Don't close socket - just stop retrying
          return;
        }

        // Handle auth token error with auto-reconnect (show modal, refresh token, reconnect)
        handleAuthTokenError(event.data);

        // Check if last 3 errors are all invalid token errors (for anonymous user segment ID regeneration)
        const lastThreeErrors = lastConnectionErrorsRef.current; //.slice(-3);
        var lastErrors = lastThreeErrors.every((err) => isInvalidTokenError(err));
        if (lastThreeErrors.length === 3 && lastErrors) {
          // Check if we've already regenerated segment ID too many times
          if (segmentIdRegenerationCountRef.current >= MAX_SEGMENT_ID_REGENERATIONS) {
            console.warn(`Max segment ID regenerations (${MAX_SEGMENT_ID_REGENERATIONS}) exceeded. Stopping.`);
            dispatch(setLastErrorMessage("Authentication failed. Please refresh the page or log in again."));
            dispatch(setShowConnectionErrorModal(true));
            dispatch(setConnectionStatus("error"));
            return;
          }

          segmentIdRegenerationCountRef.current += 1;
          console.warn(
            `Detected 3 consecutive invalid token errors. Regenerating segment ID (attempt ${segmentIdRegenerationCountRef.current}/${MAX_SEGMENT_ID_REGENERATIONS})...`,
          );
          console.warn("Error types:", lastThreeErrors);

          // Get anonymous ID (will use existing cookie or generate new one)
          try {
            await getInhouseAnonymousId();

            // Clear error history after ID retrieval
            lastConnectionErrorsRef.current = [];
          } catch (e) {
            ErrorLoggingV2(
              {
                error_type: `Failed to get anonymous ID`,
                error_message: `${JSON.stringify(e)}`,
              },
              "anonymous_id_retrieval_error",
            );
          }
        }

        return; // Don't process invalid token error further
      }

      try {
        if (event.data === "Connection successful!") {
          retryCountRef.current = 0;
          authErrorRetryCountRef.current = 0; // Reset auth error counter on truly successful connection
          segmentIdRegenerationCountRef.current = 0; // Reset segment ID regeneration counter
          // Clear error history on successful connection
          lastConnectionErrorsRef.current = [];

          // The connection is confirmed authenticated — remap any threads this
          // session created over an anonymous socket to the signed-in user.
          if (socketIdentityRef.current === "authenticated" && anonThreadIdsRef.current.size > 0) {
            recoverAnonymousThreads();
          }

          // Strip anonId from URL after successful WS connection — it's no longer needed
          const currentParams = new URLSearchParams(window.location.search);
          if (currentParams.has("anonId")) {
            currentParams.delete("anonId");
            const newUrl = currentParams.toString()
              ? `${window.location.pathname}?${currentParams.toString()}`
              : window.location.pathname;
            window.history.replaceState({}, "", newUrl);
          }

          return;
        }
      } catch (e) {
        ErrorLoggingV2(
          { error_type: `Failed to read event.data`, error_message: `${JSON.stringify(e)}` },
          "websocket_event_data_error",
        );
      }
      try {
        if (event.data) {
          message = JSON.parse(event.data);
        }
      } catch (e) {
        if (!(!event.data || JSON.stringify(event.data) === "{}")) {
          // console.log("Error parsing JSON: ", e, event);
          ErrorLoggingV2(
            {
              error_type: `Error parsing JSON`,
              error_message: `${JSON.stringify(e)}`,
              event: JSON.stringify(event),
            },
            "json_parsing_error",
          );
        }
        setAccesstoken(await getMyToken());
        connectionMutexRef.current = false;
        connectionStateRef.current = "disconnected";
        var wsError = applicationStore.getState().messageSlice.wsErrorCount || 0;
        if (wsError >= 5) {
          console.warn("Reloading page due to repeated WebSocket errors");
          window.location.reload();
        }
        dispatch(setWsErrorCount(wsError + 1));
        return;
      }
      // Allow enable_messaging messages through even if trigger_ws_message_id isn't tracked
      const isEnableMessagingMessage = message.status_code === 200 && message.payload?.type === "enable_messaging";
      // Allow 402 thread-limit errors through so the checkout modal can open (try page + chat page).
      const isThreadLimitError =
        message.status_code === 402 &&
        message.payload?.error?.error_code === "free_thread_limit_reached";

      // Allow "thread already exists" errors through so we can redirect to /home.
      const duplicateThreadErrorStr = message.payload?.error || message.error;
      const isDuplicateThreadError =
        duplicateThreadErrorStr === "Thread already exists with for the given GPT context id";

      // Get current recovery state
      const currentState = applicationStore.getState();
      const isRecoveryInProgress = currentState.messageSlice.messageReliability?.recoveryInProgress || false;
      const pendingThreadCreation = currentState.messageSlice.pendingThreadCreation;

      // Check if we're in recovery window
      const isInRecoveryWindow = recoveryWindowEndRef.current && Date.now() < recoveryWindowEndRef.current;

      // Allow messages during recovery or if they're related to pending operations
      const isRecoveryRelatedMessage =
        (isRecoveryInProgress || isInRecoveryWindow) &&
        (message.action === "chat/thread-created" ||
          message.action === "chat/add-message" ||
          message.action === "new_message" ||
          message.action === "message_added");

      // Check if this message is related to a pending thread creation
      const isPendingThreadMessage =
        pendingThreadCreation?.status === "creating" &&
        (message.action === "chat/thread-created" ||
          (message.action === "chat/add-message" && message.payload?.thread_id === pendingThreadCreation.threadId));
      if (
        requestIds?.indexOf(message.trigger_ws_message_id) === -1 &&
        !isEnableMessagingMessage &&
        !isThreadLimitError &&
        !isDuplicateThreadError &&
        !isRecoveryRelatedMessage &&
        !isPendingThreadMessage &&
        message?.payload?.thread_id !== applicationStore.getState().messageSlice?.threadData?.id
      ) {
        // Log why message was filtered during recovery window
        if (isInRecoveryWindow) {
          console.warn("Message filtered during recovery window:", {
            action: message.action,
            trigger_ws_message_id: message.trigger_ws_message_id,
            requestIds: requestIds,
          });
        }
        return;
      }
      if (message.payload?.error || message?.error || message?.status_code > 299) {
        var errorMessage = "";
        // Set by the free/subscribed message-limit branches below — those need
        // messagingDisabled to STAY true (the user is genuinely blocked from
        // sending), so they must skip the generic "re-enable messaging" reset
        // this block does for every other WS error.
        var skipMessagingDisabledReset = false;

        // ── Duplicate thread error → let the chat page handle redirect ──
        if (isDuplicateThreadError) {
          dispatch(setAwaitingResponse(false));
          dispatch(setAwaitingThreadCreationResponse(false));
          dispatch(setFailedToCreateThread(true));
          dispatch(setDuplicateThreadError(true));
          return;
        }

        // Extract structured error_code from the new BE error envelope.
        // All limit gates now return { error_code: "...", limit: N } under payload.error.
        const wsErrorCode = message.payload?.error?.error_code;

        // ── 402 gates: user can pay their way out → upgrade modal ────────
        if (message.status_code === 402 && wsErrorCode === "free_thread_limit_reached") {
          dispatch(setAwaitingThreadCreationResponse(false));
          dispatch(setFailedToCreateThread(true));
          if (window.location.pathname.includes("/try")) {
            dispatch(setSignupAlertPopup({ show: true, actionType: "thread_limit" }));
          } else {
            dispatch(openCheckoutWithEntry({ entry: "thread_limit" }));
          }
          return;
        }

        if (message.status_code === 402 && wsErrorCode === "free_user_message_limit_reached") {
          const metadata = applicationStore.getState().onboarding.userMetadata;
          dispatch(setFreeUserLimit(true));
          dispatch(setAwaitingResponse(false));
          dispatch(setMessagingDisabled(true));
          stopStatusPolling();
          trackAnalytics(
            EVENT_NAMES.RECEIVED_AI_REPLY,
            {
              [EVENT_PARAMETERS.AUTO_TRIGGER]: true,
              [EVENT_PARAMETERS.THREAD_ID]: message.payload?.thread_id,
              [EVENT_PARAMETERS.USER_ID]: metadata?.id,
              [EVENT_PARAMETERS.ERROR_MESSAGE]: wsErrorCode,
              [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
            },
            metadata,
          );
          cancelAttachmentRetry();
          return;
        }

        if (message.status_code === 402 && wsErrorCode === "free_user_attachment_limit_reached") {
          // The DocumentLimitPopup is already shown by the success handler
          // (free_user_attachment_limit_reached message arrives before this 402).
          // Just clean up state — composer stays unlocked.
          cancelAttachmentRetry();
          dispatch(setAwaitingResponse(false));
          return;
        }

        // ── 400 gates: terminal cap (already paid) → inline message, no upgrade ──
        if (message.status_code === 400 && wsErrorCode === "subscribed_user_message_limit_reached") {
          dispatch(setMessagingDisabled(true));
          dispatch(setAwaitingResponse(false));
          cancelAttachmentRetry();
          return;
        }

        if (message.status_code === 400 && wsErrorCode === "subscribed_user_attachment_limit_reached") {
          toast.error("You've reached the attachment limit for this chat. You can still send text messages.");
          trackAnalytics(EVENT_NAMES.VIEWED_ATTACHMENT_LIMIT, {
            [EVENT_PARAMETERS.THREAD_ID]: message.payload?.thread_id,
            limit: message.payload?.error?.limit,
          });
          // Composer stays unlocked — only attachments are blocked.
          cancelAttachmentRetry();
          dispatch(setAwaitingResponse(false));
          return;
        }

        // Handle new format errors
        if (message.status_code === 404 && message.payload?.error === "GPTContextNotFoundException") {
          dispatch(setGPTContextError(true));
          dispatch(setFailedToCreateThread(true));
          errorMessage = "GPT context not found. Please try again.";
        } else if (message.status_code >= 400 && message.payload?.error === "GPTThreadAlreadyAvailableException") {
          dispatch(setFailedToCreateThread(true));
          dispatch(setAwaitingResponse(false));
          dispatch(setAwaitingThreadCreationResponse(false));
          dispatch(setDuplicateThreadError(true));
          return;
        } else if (message.status_code >= 400 && message.payload?.error?.indexOf("AIInProgressException") > -1) {
          dispatch(setMessagingDisabled(false));
          return;
        } else if (message.payload?.error === "AttachmentsStillProcessing") {
          // Backend isn't ready yet — schedule retry, keep shimmer up.
          const state = applicationStore.getState().messageSlice;
          const threadId = state.threadData?.id;
          const lastMessageId = state.threadData?.lastMessage?.id;
          if (threadId && lastMessageId) {
            scheduleAttachmentRetry(threadId, lastMessageId, state.buttonClickTypeForTrigger);
          }
          return;
        } else if (
          message.payload?.error?.indexOf("MessagingDisabledException") > -1 &&
          !areEmailThreadsTerminal(
            collectEmailAttachmentThreadIds(applicationStore.getState().messageSlice.threadData?.messages),
            applicationStore.getState().emailContext?.attachmentStatusByThreadId,
          )
        ) {
          // Backstop for the reload path: after a refresh the trigger is fired
          // from history (deriveNeedsAiTrigger) rather than from a live WS batch,
          // so the deferral above never ran and the email send gate rejects it
          // here. Same treatment — hold it, fire it when the poll goes terminal.
          // Scoped to threads that actually carry a non-terminal email chip, so
          // an ordinary messaging lock still falls through to generic handling.
          const state = applicationStore.getState().messageSlice;
          const threadId = state.threadData?.id;
          const lastMessageId = state.lastTriggeredMessageId || state.threadData?.lastMessage?.id;
          if (threadId && lastMessageId) {
            deferredEmailTriggerRef.current = {
              threadId,
              lastMessageId,
              buttonClickType: state.buttonClickTypeForTrigger || null,
              emailThreadIds: collectEmailAttachmentThreadIds(state.threadData?.messages),
            };
            dispatch(setAwaitingResponse(true));
            dispatch(setMessagingDisabled(true));
          }
          return;
        } else if (message.payload?.error?.indexOf("AwaitingTriggerAIMessageException") > -1) {
          // Stale messaging_disabled lock — AI trigger was never received after add-message.
          // Extract thread_id and last_message_id from the error string and re-send trigger-ai-response.
          const errorStr = message.payload.error;
          const threadIdMatch = errorStr.match(/thread_id:\s*([^\s|]+)/);
          const lastMsgIdMatch = errorStr.match(/last_message_id:\s*([^\s|]+)/);
          const recoveryThreadId = threadIdMatch?.[1] || message.payload?.thread_id;
          const recoveryLastMessageId = lastMsgIdMatch?.[1] || null;

          if (recoveryThreadId && recoveryLastMessageId) {
            const triggerAIPayload = {
              thread_id: recoveryThreadId,
              last_message_id: recoveryLastMessageId,
              button_click_type: null,
            };
            const triggerAIMessage = createMessage("ask", "trigger-ai-response", triggerAIPayload);
            const aiTriggerSuccess = sendMessage(triggerAIMessage, true);
            const activeThreadId = applicationStore.getState().messageSlice.threadData?.id;
            if (aiTriggerSuccess && recoveryThreadId === activeThreadId) {
              dispatch(setAwaitingResponse(true));
              dispatch(setMessagingDisabled(true));
            }
          } else {
            dispatch(setAwaitingResponse(false));
            dispatch(setMessagingDisabled(false));
          }
          return;
        } else {
          switch (message.action) {
            case "chat/thread-not-created":
              if (message.payload?.error?.search("threads limit reached") > -1) {
                errorMessage = message.payload.error;
              }
              errorMessage = `Error creating thread. ${errorMessage || ""}`;
              break;
            case "chat/create-thread":
            case "chat/thread-created":
              if (message.payload?.error?.search("FileNotReadableError") > -1) {
                errorMessage = "Unable to read file. Please try again";
              } else {
                errorMessage = "Error creating thread";
              }
              dispatch(setFailedToCreateThread(true));
              break;
            case "chat/add-message": {
              errorMessage = "Error adding message";
              // Purge any unsent-message entries whose thread_id matches the
              // rejected payload (or is null). Without this, the recovery loop
              // retries the same broken payload on every reconnect, causing the
              // rapid-fire rejection bursts the backend observed.
              const rejectedThreadId = message.payload?.thread_id;
              const currentUnsent =
                applicationStore.getState().messageSlice.messageReliability?.unsentMessages || {};
              Object.entries(currentUnsent).forEach(([msgId, msg]) => {
                if (!msg.threadId || msg.threadId === rejectedThreadId) {
                  dispatch(removeUnsentMessage(msgId));
                }
              });
              break;
            }
            case "chat/new-message-available":
              errorMessage = "Failed to generate AI response. Please try again";
              break;
            case "chat/submit-and-create-document":
            case "chat/create-document":
              errorMessage = "Failed to create document. Please try again";
              break;
            case "chat/document-created":
            case "chat/unlock-document":
              try {
                if (
                  message.payload?.error?.error?.search("Document already created for given thread") > -1 ||
                  message.payload?.error?.search("Document already created for given thread") > -1
                ) {
                  errorMessage = null;
                  const threadData = applicationStore.getState().messageSlice.threadData;
                  if (window.location.pathname.toLowerCase().search("shared") === -1) {
                    dispatch(
                      getAskThreadMessages({ threadId: threadData.id, dispatchSource: "doc-already-created-error" }),
                    );
                  }
                  // dispatch(setDocumentIsLocked(false));
                } else {
                  errorMessage = "Error creating document";
                }
              } catch (e) { }
              break;
            case "chat/inferred-metadata":
              errorMessage = "Error inferring metadata";
              break;

            default:
              errorMessage = "";
              break;
          }
        }
        if (errorMessage) {
          toast.error(errorMessage);
        }
        if (JSON.stringify(message)?.indexOf("LastMessageDifferentException") === -1) {
          ErrorLoggingV2(
            {
              name: `Backend WS error: ${errorMessage}: ${message?.action}`,
              message: errorMessage,
              payload: JSON.stringify(message),
            },
            `backend_ws_error: ${message?.action}`,
          );
        }
        if (message?.payload?.allow_regeneration) {
          dispatch(addMessage({ new_messages: [{ message_type: "regenerate_ai_response" }] }));
        }
        // Any non-AttachmentsStillProcessing error means the trigger flow is dead — stop retrying.
        cancelAttachmentRetry();
        dispatch(setAwaitingResponse(false));
        if (!skipMessagingDisabledReset) {
          dispatch(setMessagingDisabled(false));
        }
        return;
      }

      var scroll = applicationStore.getState().messageSlice.scroll;
      var chatInputMessage = applicationStore.getState().messageSlice.chatInputMessage;
      var queryParams = new URLSearchParams(window.location.search);
      var userMetadata = applicationStore.getState().onboarding.userMetadata;

      if (message.status_code === 200 && message.payload?.type) {
        switch (message.payload.type) {
          case "chat_created": {
            // Handle ChatCreatedResponse - Two-step flow Phase 2 completion
            const threadId = message.payload.thread_id;
            if (socketIdentityRef.current === "anonymous") {
              anonThreadIdsRef.current.add(threadId);
              rememberAnonymousThreadId(threadId);
            }
            dispatch(setAwaitingThreadCreationResponse(false));
            // First Chat detection — fires before insertNewThread so thread count is pre-insert

            const isFirstChatTryPage = window.location.pathname === "/try";
            const existingThreads = applicationStore.getState().threads.threads;
            const hasNoExistingThreads =
              !existingThreads ||
              (Array.isArray(existingThreads) && existingThreads.length === 0) ||
              (!Array.isArray(existingThreads) &&
                Object.values(existingThreads).every((bucket) => !Array.isArray(bucket) || bucket.length === 0));
            const isFirstChatLoggedIn = window.location.pathname !== "/try" && hasNoExistingThreads;

            if (isFirstChatTryPage || isFirstChatLoggedIn) {
              trackAnalytics(
                EVENT_NAMES.FIRST_CHAT,
                {
                  [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
                  [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
                  [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
                },
                userMetadata,
              );
              trackPixelEvent("Lead");
            }

            dispatch(setIsNewThreadCreted(true));
            dispatch(
              setupNewThread({
                threadId: threadId,
                threadType: "ask",
                messageId: null,
                new_messages: [],
              }),
            );
            dispatch(
              insertNewThread({
                thread_id: threadId,
                thread_type: "chat",
                messaging_disabled: false,
                id: threadId,
              }),
            );

            // Optimistically decrement free_threads_remaining so the
            // thread-limit banner appears instantly when the user returns
            // to the home page (no refresh needed).
            dispatch(decreaseThreadsRemaining());

            // Replay any message_added frame for this thread that arrived (and got
            // buffered) before this chat_created was processed. threadData.id is
            // now set by the dispatches above, so re-entering receiveMessage lets
            // the message_added guard pass this time and process normally.
            const bufferedFrameData = pendingMessageAddedFramesRef.current[threadId];
            if (bufferedFrameData) {
              delete pendingMessageAddedFramesRef.current[threadId];
              receiveMessage({ data: bufferedFrameData });
            }

            // Update pending thread creation status
            dispatch(
              updatePendingThreadStatus({
                status: "created",
                threadId: threadId,
              }),
            );

            // CRITICAL: Persist threadId to localStorage for recovery after page refresh
            const persistSuccess = PersistentStorage.updatePendingMessageThreadId(threadId);
            if (!persistSuccess) {
              console.warn("Failed to persist threadId to localStorage - recovery may create duplicate thread");
            }

            // Get pending message data from Redux state
            const state = applicationStore.getState();
            const pendingThreadCreation = state.messageSlice.pendingThreadCreation;

            if (pendingThreadCreation?.message && pendingThreadCreation.transactionId) {
              // Step 2: Send the pending message to the newly created thread
              const messagePayload = {
                thread_id: threadId,
                message: pendingThreadCreation.message,
              };

              // Add attachments if present
              if (pendingThreadCreation.attachments && pendingThreadCreation.attachments.length > 0) {
                messagePayload.reference_doc_storage_ids = pendingThreadCreation.attachments;
              }

              // Add button click type if present
              if (pendingThreadCreation.buttonClickType) {
                messagePayload.button_click_type = pendingThreadCreation.buttonClickType;
              }

              // Update transaction status
              updateTransactionStatus(pendingThreadCreation.transactionId, "MESSAGE_SENDING", {
                threadId: threadId,
              });

              // Create and send the message
              const wsMessage = createMessage("ask", "add-message", messagePayload);
              const success = sendMessage(wsMessage, true);

              if (success) {
                // Mark status as "message_sending" immediately so the reconnection recovery
                // logic (which only fires on status === "created") does not resend the message.
                dispatch(updatePendingThreadStatus({ status: "message_sending" }));
              } else {
                // Handle message send failure
                updateTransactionStatus(pendingThreadCreation.transactionId, "MESSAGE_SEND_FAILED", {
                  error: "Failed to send WebSocket message",
                });

                // Add to unsent messages for retry
                dispatch(
                  addUnsentMessage({
                    id: `msg_${Date.now()}`,
                    threadId: threadId,
                    message: pendingThreadCreation.message,
                    attachments: pendingThreadCreation.attachments || [],
                    buttonClickType: pendingThreadCreation.buttonClickType,
                    status: "failed",
                    retryCount: 0,
                    transactionId: pendingThreadCreation.transactionId,
                  }),
                );

                toast.error("Failed to send message. It will be retried automatically.");
              }
            } else {
              // No pending message - just mark thread creation as complete
              // User will be redirected to thread page
              dispatch(clearPendingThreadCreation());

              // Clear persistent storage
              PersistentStorage.clearPendingMessage();
            }

            // Track thread creation on every page (INH-5847, 2026-11-03): it was
            // /try only, so logged-in new chats never reached Cometly's New Chat
            // event. First Chat and the Lead pixel already fired above.
            const trackedThreadsKey = "tracked_thread_creations";
            const trackedThreads = JSON.parse(sessionStorage.getItem(trackedThreadsKey) || "{}");

            if (!trackedThreads[threadId]) {
              trackAnalytics(
                EVENT_NAMES.STARTED_NEW_CHAT,
                {
                  [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
                  [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
                  [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
                  [EVENT_PARAMETERS.UTM_CAMPAIGN]: queryParams.get("utm_campaign"),
                  [EVENT_PARAMETERS.UTM_MEDIUM]: queryParams.get("utm_medium"),
                  [EVENT_PARAMETERS.UTM_THREAD_ID]: threadId,
                  [EVENT_PARAMETERS.UTM_SOURCE]: queryParams.get("utm_source"),
                },
                userMetadata,
              );
              trackedThreads[threadId] = true;
              sessionStorage.setItem(trackedThreadsKey, JSON.stringify(trackedThreads));
            }

            // Redirect immediately
            const stateAfterSetup = applicationStore.getState();
            const messageThreadId = stateAfterSetup.messageSlice.threadData?.id;

            if (messageThreadId && isConnectedRef.current) {
              // console.log("Redirecting immediately after thread creation:", threadId);
              dispatch({
                type: "messageSlice/setRedirectTo",
                payload: `/chat/${threadId}`,
              });
            } else if (messageThreadId && !isConnectedRef.current) {
              console.warn("Skipping redirect due to disconnection, will redirect after reconnection");
              sessionStorage.setItem("pendingRedirect", `/chat/${threadId}`);
            }
            break;
          }

          case "message_added": {
            // Handle MessagesAddedResponse - Two-step flow Phase 3 completion
            // NEW: This now also handles quick_actions messages within new_messages array
            // Guard: only process if this message belongs to the currently loaded thread.
            // Without this, streaming messages from thread-A leak into thread-B when
            // the user switches mid-stream (same pattern as chat/new-message-available).
            const currentThread = applicationStore.getState().messageSlice.threadData;
            if (currentThread?.id !== message.payload.thread_id) {
              // Narrow exception: if we're mid-flight creating a new thread, this
              // frame may simply have arrived before its own chat_created was
              // processed (backend doesn't guarantee that ordering — see the
              // email-attachment V3.3 handoff doc). Buffer it and replay once
              // chat_created lands, instead of dropping it. Anything else (a
              // genuinely unrelated thread) is still dropped exactly as before.
              //
              // "pending" is the normal in-flight status set by
              // createThreadWithoutMessage's setPendingThreadCreation dispatch
              // (its reducer always writes "pending", regardless of the
              // "status" field passed to it — the "creating" value that call
              // site passes is not what ends up in state); "creating" itself
              // is only reached via the reconnect-recovery retry path in this
              // same file. Both represent "a chat_created is expected soon".
              const pendingThreadCreationForBuffer = applicationStore.getState().messageSlice.pendingThreadCreation;
              if (
                pendingThreadCreationForBuffer?.status === "pending" ||
                pendingThreadCreationForBuffer?.status === "creating"
              ) {
                pendingMessageAddedFramesRef.current[message.payload.thread_id] = event.data;
              }
              break;
            }
            if (message.payload.new_messages) {
              const responseTimingState = applicationStore.getState().messageSlice;
              recordResponseMilestones({
                messages: message.payload.new_messages,
                threadId: message.payload.thread_id,
                startedAt: responseTimingState.awaitingResponseStartTime,
                track: (eventName, properties) => trackAnalytics(eventName, properties, usermetadata),
              });

              // Doc-gen timeout: start timer on generate_document loading message,
              // clear it when the corresponding document message arrives.
              const newMsgsForDocGen = message.payload.new_messages;
              // Doc-gen timeout: pick the last generate_document loading message.
              // Only start a timer if no document message appears after it in the batch.
              const lastDocGenLoadingIdx = newMsgsForDocGen.findLastIndex(
                (m) =>
                  m.message_type === MESSAGE_TYPES.UPDATE_LOADING_MESSAGE &&
                  m.payload?.tool_executing_payload?.tool_name === TOOL_NAMES.GENERATE_DOCUMENT &&
                  m.trigger_id,
              );

              if (lastDocGenLoadingIdx !== -1) {
                const lastDocGenLoading = newMsgsForDocGen[lastDocGenLoadingIdx];
                // Check if a document message with the same trigger_id appears after it
                const hasDocAfter = newMsgsForDocGen.slice(lastDocGenLoadingIdx + 1).some(
                  (m) => m.message_type === MESSAGE_TYPES.DOCUMENT && m.trigger_id === lastDocGenLoading.trigger_id,
                );
                // Also check if it already exists in the thread messages
                const existingMessages = applicationStore.getState().messageSlice.threadData?.messages || [];
                const docAlreadyExists = existingMessages.some(
                  (m) => m.message_type === MESSAGE_TYPES.DOCUMENT && m.trigger_id === lastDocGenLoading.trigger_id,
                );

                if (hasDocAfter || docAlreadyExists) {
                  clearDocGenTimer(lastDocGenLoading.trigger_id);
                } else {
                  startDocGenTimer(lastDocGenLoading.trigger_id, message.payload.thread_id);
                }
              }

              // Clear timers for any document messages arriving (potentially from a different batch)
              const docGenResults = newMsgsForDocGen.filter(
                (m) => m.message_type === MESSAGE_TYPES.DOCUMENT && m.trigger_id,
              );
              for (const doc of docGenResults) {
                clearDocGenTimer(doc.trigger_id);
              }

              // Auto-unlock AI-generated documents for paid plan users:
              // Add document_payment BEFORE processing messages so the reducer sees it
              const msgAddedMetadata = applicationStore.getState().onboarding.userMetadata;
              if (Plan.from(msgAddedMetadata?.subscription_type).isPaid()) {
                const docMsg = message.payload.new_messages.find(
                  (msg) => msg.message_type === MESSAGE_TYPES.DOCUMENT && !msg.is_user_uploaded,
                );
                if (docMsg?.doc_id) {
                  dispatch(addDocumentPayment({ doc_id: docMsg.doc_id }));
                }
              }

              // Fire Received Draft for any document in this batch
              const docMsgForDraft = message.payload.new_messages.find(
                (msg) => msg.message_type === MESSAGE_TYPES.DOCUMENT,
              );
              if (docMsgForDraft) {
                const wsThreadData = applicationStore.getState().messageSlice.threadData;
                const docIsLocked = docMsgForDraft.is_user_uploaded
                  ? false
                  : !wsThreadData?.document_payments?.find((p) => p.doc_id === docMsgForDraft.doc_id)?.has_doc_unlocked;
                trackAnalytics(
                  EVENT_NAMES.RECEIVED_DRAFT,
                  {
                    [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
                    [EVENT_PARAMETERS.THREAD_ID]: message.payload.thread_id,
                    [EVENT_PARAMETERS.DOCUMENT_ID]: docMsgForDraft.doc_id,
                    [EVENT_PARAMETERS.DOCUMENT_VERSION]: docMsgForDraft.version,
                    [EVENT_PARAMETERS.IS_LOCKED]: docIsLocked,
                    [EVENT_PARAMETERS.IS_USER_DOCUMENT]: docMsgForDraft.is_user_uploaded,
                    [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
                  },
                  userMetadata,
                );
              }

              // Document Edited By User (INH-5930): any edited version of a generated doc in this batch
              message.payload.new_messages
                .filter((msg) => msg.message_type === MESSAGE_TYPES.DOCUMENT)
                .forEach((docMessage) =>
                  trackDocumentEditedByUser({ docMessage, threadId: message.payload.thread_id, metadata: userMetadata }),
                );

              // Process all messages (including quick_actions) through handleGPTContextMessages
              // which now uses insertNewMessages for proper quick_actions transformation
              dispatch(
                handleGPTContextMessages({
                  new_messages: message.payload.new_messages,
                  thread_id: message.payload.thread_id,
                }),
              );

              // Subscribed user message thresholds — fire analytics once on first WS delivery.
              // Both message types are persisted so they appear on reload; analytics must NOT fire
              // on component mount — only here when the WS batch first carries the message.
              const newMsgs = message.payload.new_messages;
              const hasHighContextWarning = newMsgs.some((m) => m.message_type === "high_context_warning");
              if (hasHighContextWarning) {
                trackAnalytics(EVENT_NAMES.VIEWED_HIGH_CONTEXT_WARNING, {
                  thread_id: message.payload.thread_id,
                });
              }
              const hasSubscribedLimitReached = newMsgs.some(
                (m) => m.message_type === "subscribed_message_limit_reached",
              );
              if (hasSubscribedLimitReached) {
                dispatch(setMessagingDisabled(true));
                dispatch(setAwaitingResponse(false));
                trackAnalytics(EVENT_NAMES.VIEWED_SUBSCRIBED_MESSAGE_LIMIT, {
                  thread_id: message.payload.thread_id,
                });
              }

              // Free user attachment limit — backend sends this ephemeral message
              // when the cumulative attachment cap is exceeded. Show the modal.
              const hasFreeAttachmentLimit = newMsgs.some(
                (m) => m.message_type === "free_user_attachment_limit_reached",
              );
              if (hasFreeAttachmentLimit) {
                dispatch(setShowDocumentLimitPopup(true));
              }

              // Privileged-chat markers — fire analytics on first WS delivery only.
              const hasPrivilegedStart = newMsgs.some((m) => m.message_type === "privileged_chat_started");
              if (hasPrivilegedStart) {
                dispatch(setThreadPrivileged(message.payload.thread_id));
                trackAnalytics(EVENT_NAMES.VIEWED_BANNER, {
                  [EVENT_PARAMETERS.BANNER_TYPE]: BANNER_TYPES.PRIVILEGED_CHAT_STARTED,
                  thread_id: message.payload.thread_id,
                });
              }
              const hasPrivilegedEnd = newMsgs.some((m) => m.message_type === "privileged_chat_ended");
              if (hasPrivilegedEnd) {
                trackAnalytics(EVENT_NAMES.VIEWED_BANNER, {
                  [EVENT_PARAMETERS.BANNER_TYPE]: BANNER_TYPES.PRIVILEGED_CHAT_ENDED,
                  thread_id: message.payload.thread_id,
                });
              }

              // Update attachment quota from the response (null = no update, skip).
              if (message.payload.attachment_info != null) {
                dispatch(setAttachmentInfo(message.payload.attachment_info));
              }

              // Always open/switch the doc panel when a new document arrives via WS,
              // regardless of whether the panel is already open on a different doc.
              // v0 user-uploaded docs are surfaced as attachment pills, not in the panel — skip them.
              const newDocMessage = message.payload.new_messages.find(
                (m) => m.message_type === MESSAGE_TYPES.DOCUMENT && !m.is_user_uploaded && Number(m.version) !== 0,
              );
              if (newDocMessage) {
                dispatch(setActiveDocument({ doc_id: newDocMessage.doc_id, version: newDocMessage.version }));
              }

              // Update local memory version when AI updates profile memory
              const memoryMsg = message.payload.new_messages.find((m) => m.message_type === "memory_updated");
              if (memoryMsg?.new_version) {
                dispatch(updateVersionFromWS(memoryMsg.new_version));
              }

              const hasTextDelta = message.payload.new_messages.some((m) => m.message_type === "text_delta");
              if (hasTextDelta) {
                recordStreamFrame();
                startStreamingRecoveryPoll(message.payload.thread_id);
                // AI is responding — stop the AttachmentsStillProcessing retry loop.
                cancelAttachmentRetry();
              }

              // Check if this is a GPT context thread that just received its initial messages
              const isGPTContextThread = message.payload.new_messages.some(
                (msg) =>
                  msg.message_type === "law_gpt_summary" ||
                  msg.message_type === "law_gpt_summary_ask" ||
                  msg.message_type === "law_gpt_additional_details",
              );

              // Get current state to check thread ID
              const gptContextState = applicationStore.getState();
              const gptContextCurrentThreadId = gptContextState.messageSlice.threadData?.id;
              if (
                isGPTContextThread &&
                (message.payload.thread_id === gptContextCurrentThreadId || !gptContextCurrentThreadId)
              ) {
                // Enable messaging for GPT context threads after initial message
                dispatch(setMessagingDisabled(false));
                dispatch(setAwaitingResponse(false));
              }
            }

            // Extract last message ID directly from WS payload for immediate trigger
            let lastMessageId = null;
            if (message.payload.new_messages && message.payload.new_messages.length > 0) {
              if (message.payload.new_messages.some((msg) => msg.message_type === "regenerate_ai_response")) {
                // Failed-turn marker is terminal — no enable_messaging follows.
                // Clear the awaiting-response timers (setAwaitingResponse(false)
                // resets awaitingResponseStartTime, which WebSocketMonitor keys
                // off to cancel the 90s/150s slow-response events) and keep the
                // composer blocked so the Regenerate affordance is the way forward.
                dispatch(setAwaitingResponse(false));
                dispatch(setMessagingDisabled(true));
                // If a retry was in flight, this marker means the retry failed
                // again — record it, then clear the retry-in-progress flag.
                const regenState = applicationStore.getState().messageSlice;
                if (regenState.regenerationHappening) {
                  trackAnalytics(EVENT_NAMES.REGENERATE_FAILED, {
                    [EVENT_PARAMETERS.THREAD_ID]: regenState.threadData?.id || message.payload.thread_id,
                    last_message_id: regenState.lastTriggeredMessageId,
                  });
                  dispatch(setRegenerationHappening(false));
                }
              } else if (message.payload.new_messages.some((msg) => msg.message_type === MESSAGE_TYPES.DOCUMENT)) {
              } else {
                const triggerableMessages = message.payload.new_messages
                  .filter((msg) => msg.is_user_message === true || msg.message_type === "attachment")
                  .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                if (triggerableMessages.length > 0) {
                  lastMessageId = triggerableMessages[triggerableMessages.length - 1].id;
                }
              }
            }

            const currentState = applicationStore.getState();
            const pendingCreation = currentState.messageSlice.pendingThreadCreation;

            // Clear chatbox after message has been successfully added
            dispatch(setChatInputMessage(""));

            // Trigger AI response for ALL message_added responses (not just first ones)
            // Use payload thread_id as fallback — threadData.id may not be set yet on first message
            const messageThreadId = currentState.messageSlice.threadData?.id || message.payload.thread_id;

            // Email-context send gate. When this batch carries an `email_attachment`
            // chip that's still extracting, firing now would be rejected by the
            // backend with MessagingDisabledException — an error indistinguishable
            // from a genuine lock, so nothing would retry and the turn would stall
            // until the user reloads the page. Hold the trigger and let the effect
            // below fire it once the 5s status poll reports every thread terminal.
            const pendingEmailThreadIds = findPendingEmailAttachmentThreadIds(message.payload.new_messages);
            if (messageThreadId && lastMessageId && pendingEmailThreadIds.length > 0) {
              deferredEmailTriggerRef.current = {
                threadId: messageThreadId,
                lastMessageId,
                buttonClickType: currentState.messageSlice.buttonClickTypeForTrigger || null,
                emailThreadIds: pendingEmailThreadIds,
              };
              dispatch(setButtonClickTypeForTrigger(null));
              // The turn is live from the user's point of view — extraction is
              // just its first phase — so hold the shimmer and the composer lock
              // rather than flashing an idle composer before the AI starts.
              dispatch(setAwaitingResponse(true));
              dispatch(setMessagingDisabled(true));
              dispatch(setShouldTriggerAIResponse(false));
            } else if (messageThreadId && lastMessageId) {
              // Send trigger-ai-response message with required last_message_id
              const buttonClickType = currentState.messageSlice.buttonClickTypeForTrigger;
              const triggerAIPayload = {
                thread_id: messageThreadId,
                last_message_id: lastMessageId,
                button_click_type: buttonClickType || null,
              };

              const triggerAIMessage = createMessage("ask", "trigger-ai-response", triggerAIPayload);
              const aiTriggerSuccess = sendMessage(triggerAIMessage, true);
              dispatch(setButtonClickTypeForTrigger(null));
              if (aiTriggerSuccess) {
                // Remember the user message this turn is answering so a later
                // regenerate can resend the exact same last_message_id.
                dispatch(setLastTriggeredMessageId(lastMessageId));
                // Set awaiting response state
                dispatch(setAwaitingResponse(true));
                dispatch(setMessagingDisabled(true));

                // Clear the shouldTriggerAIResponse flag to prevent duplicate triggers
                // from ChatBoxComponent useEffect
                dispatch(setShouldTriggerAIResponse(false));

                // Poll /status in case the enable_messaging WS response is lost
                startStatusPolling(messageThreadId);
              } else {
                console.warn("Failed to trigger AI response");
                // Don't fail the whole flow, just log the issue
              }
            } else if (lastMessageId && !messageThreadId) {
              // We have a user/attachment message to respond to but no thread id — a real error.
              console.warn("Cannot trigger AI response: user message present but missing threadId", {
                lastMessageId,
              });
            }
            // Otherwise there is no triggerable (user/attachment) message in this batch — the
            // common case for AI streaming frames, documents, activity signals, and regenerate
            // acks. There is nothing to trigger, so skip silently rather than warning on every
            // streamed frame (which previously flooded the console during a turn).

            // Handle pending thread creation completion (for first messages only)
            if (
              (pendingCreation?.status === "created" || pendingCreation?.status === "message_sending") &&
              pendingCreation.transactionId
            ) {
              // Message successfully added - complete the transaction
              updateTransactionStatus(pendingCreation.transactionId, "COMPLETED", {
                completedAt: Date.now(),
              });

              // Clear pending thread creation
              dispatch(clearPendingThreadCreation());

              // Clear persistent storage
              PersistentStorage.clearPendingMessage();

              // Remove from unsent messages if it was there
              const unsentMessages = currentState.messageSlice.messageReliability?.unsentMessages || {};
              Object.keys(unsentMessages).forEach((messageId) => {
                const unsentMessage = unsentMessages[messageId];
                if (unsentMessage.transactionId === pendingCreation.transactionId) {
                  dispatch(removeUnsentMessage(messageId));
                }
              });
            }
            break;
          }

          case "new_title":
            // Handle NewTitleResponse
            dispatch(
              updateThreadTitleLocal({
                thread_id: message.payload.thread_id,
                title: message.payload.title,
              }),
            );
            dispatch(
              updateThreadDataTitle({
                thread_id: message.payload.thread_id,
                title: message.payload.title,
              }),
            );
            break;

          case "enable_messaging":
            // Clear the messaging enable timer since we received enable_messaging
            if (messagingEnableTimeoutRef.current) {
              clearTimeout(messagingEnableTimeoutRef.current);
              messagingEnableTimeoutRef.current = null;
            }
            stopStatusPolling();
            stopStreamingRecoveryPoll();

            // Handle enable_messaging response - validate thread_id matches current thread
            const enableMessagingState = applicationStore.getState();
            const enableMessagingCurrentThreadId = enableMessagingState.messageSlice.threadData?.id;

            if (message.payload.thread_id === enableMessagingCurrentThreadId) {
              dispatch(finalizeStreamingMessage());
              dispatch(setMessagingDisabled(false));
              dispatch(setAwaitingResponse(false));
              // Turn completed successfully — a retry (if any) succeeded, so clear
              // the retry-in-progress flag.
              if (enableMessagingState.messageSlice.regenerationHappening) {
                dispatch(setRegenerationHappening(false));
              }
            } else {
            }
            break;

          case "locked_document_generated":
            handleLockedDocumentGenerated(message);
            break;

          case "document_generated":
            handleDocumentGenerated(message);
            break;

          case "quick_action_button_messages":
          case "quick_action_buttons":
            handleQuickActionButtonMessages(message);
            break;

          case "quick_actions":
            // Handle standalone quick_actions message
            handleQuickActionsMessage(message);
            break;

          case "update_loading_message":
            // Activity Signals: thinking, tool execution, and web-search payloads
            // are persisted as message rows in true
            // position so they render as a collapsible activity log above the answer
            // and survive reload. Handled independently of the legacy animation hint —
            // a row could in principle carry both.
            if (isActivityMessage(message)) {
              dispatch(addMessage({ new_messages: [message] }));
            }
            // Legacy loading animation hint — drives the rotating Shimmer. Not persisted.
            const loadingMessageType = message.payload?.loading_message_type;
            if (loadingMessageType) {
              dispatch(updateLoadingMessageType(loadingMessageType));
            }
            // Start doc-gen timeout timer when generate_document tool begins executing
            if (message.payload?.tool_executing_payload?.tool_name === TOOL_NAMES.GENERATE_DOCUMENT) {
              const triggerId = message.payload?.trigger_id;
              const threadId = applicationStore.getState().messageSlice.threadData?.id;
              if (triggerId) {
                startDocGenTimer(triggerId, threadId);
              }
            }
            break;

          case "product_education_warning": {
            dispatch(
              addMessage({
                new_messages: [
                  {
                    id: `product_education_warning_${Date.now()}`,
                    is_user_message: false,
                    created_at: new Date().toISOString(),
                    message_type: "product_education_warning",
                    payload: null,
                    trigger_id: null,
                  },
                ],
              }),
            );
            dispatch(setAwaitingResponse(false));
            dispatch(setMessagingDisabled(false));
            break;
          }
          case "heartbeat": {
            // Backend keepalive during agent-nexus DocStart→DocComplete silent
            // windows so ALB doesn't RST the idle WS mid-draft. Stamp the
            // shared streaming-activity tracker — heartbeats prove the WS is
            // alive, so list-messages dispatchers should treat us as actively
            // streaming and skip their REST reconcile.
            recordStreamFrame();
            break;
          }
          default:
            // console.log("Unknown response type:", message.payload.type);
            break;
        }
        return; // Exit early after handling new format
      }

      switch (message.action) {
        case "ai_title_available":
          // Event 97:
          trackAnalytics(
            EVENT_NAMES.RECEIVED_AI_REPLY,
            {
              [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
              [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
              [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
              [EVENT_PARAMETERS.UTM_CAMPAIGN]: queryParams?.get("utm_campaign"),
              [EVENT_PARAMETERS.UTM_MEDIUM]: queryParams?.get("utm_medium"),
              [EVENT_PARAMETERS.UTM_THREAD_ID]: queryParams?.get("thread_id"),
              [EVENT_PARAMETERS.UTM_SOURCE]: queryParams?.get("utm_source"),
            },
            userMetadata,
          );
          dispatch(updateThreadTitleLocal(message.payload));
          dispatch(updateThreadDataTitle(message.payload));
          break;

        case "chat/thread-created":
          dispatch(setAwaitingThreadCreationResponse(false));
          if (socketIdentityRef.current === "anonymous") {
            anonThreadIdsRef.current.add(message.payload.thread_id);
            rememberAnonymousThreadId(message.payload.thread_id);
          }

          if (window.location.pathname === "/try") {
            // Check if we've already tracked this thread creation
            const trackedThreadsKey = "tracked_thread_creations";
            const trackedThreads = JSON.parse(sessionStorage.getItem(trackedThreadsKey) || "{}");

            if (!trackedThreads[message.payload.thread_id]) {
              // Event 98:
              trackAnalytics(
                EVENT_NAMES.STARTED_NEW_CHAT,
                {
                  [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
                  [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
                  [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
                  [EVENT_PARAMETERS.UTM_CAMPAIGN]: queryParams.get("utm_campaign"),
                  [EVENT_PARAMETERS.UTM_MEDIUM]: queryParams.get("utm_medium"),
                  [EVENT_PARAMETERS.UTM_THREAD_ID]: message.payload.thread_id,
                  [EVENT_PARAMETERS.UTM_SOURCE]: queryParams.get("utm_source"),
                },
                userMetadata,
              );

              // Mark this thread as tracked
              trackedThreads[message.payload.thread_id] = true;
              sessionStorage.setItem(trackedThreadsKey, JSON.stringify(trackedThreads));
            }
          }

          dispatch(
            setupNewThread({
              threadId: message.payload.thread_id,
              threadType: "ask",
              messageId: message.payload.message_id,
              new_messages: message.payload.new_messages,
            }),
          );
          dispatch(
            insertNewThread({
              thread_id: message.payload.thread_id,
              thread_type: "chat",
              messaging_disabled: false, // Default to false, will be set by enable_messaging response
              id: message.payload.thread_id,
            }),
          );
          // Optimistically decrement free_threads_remaining so the
          // thread-limit banner appears instantly when the user returns
          // to the home page (no refresh needed).
          dispatch(decreaseThreadsRemaining());

          // // console.log("Thread created: ", event.data);
          break;

        case "chat/regeneration-not-allowed":
          dispatch(setRegenerationHappening(false));
          ErrorLoggingV2(
            { error_type: `Regeneration not allowed`, error_message: JSON.stringify(message) },
            "regeneration_not_allowed",
          );
          break;
        case "chat/inferred-metadata":
          // // console.log("Inferred metadata: ", event.data);
          dispatch(setAskToDraftIntentWSResponse(message.payload));
          dispatch(setAwaitingResponse(false));
          break;
        case "chat/message-added":
          var currentThreadData = applicationStore.getState().messageSlice.threadData;
          var userMessageCount = 0;
          if (currentThreadData && currentThreadData.messages) {
            userMessageCount =
              currentThreadData.messages.filter((msg) => msg.is_user_message === true || msg.source === "user").length +
              1;
          } else {
            userMessageCount = 1;
          }
          // Event 99:
          trackAnalytics(
            EVENT_NAMES.SENT_MESSAGE,
            {
              [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
              [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
              [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
              [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.SEND_MESSAGE,
              [EVENT_PARAMETERS.MESSAGE_NUMBER]: userMessageCount,
            },
            userMetadata,
          );

          // Event 100:
          trackAnalytics(
            EVENT_NAMES.RECEIVED_AI_REPLY,
            {
              [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
              [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
              [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
              [EVENT_PARAMETERS.UTM_CAMPAIGN]: queryParams?.get("utm_campaign"),
              [EVENT_PARAMETERS.UTM_MEDIUM]: queryParams?.get("utm_medium"),
              [EVENT_PARAMETERS.UTM_THREAD_ID]: queryParams?.get("thread_id"),
              [EVENT_PARAMETERS.UTM_SOURCE]: queryParams?.get("utm_source"),
            },
            userMetadata,
          );
          // // console.log("Message added: ", event.data);
          var payload = message.payload;
          var threadData = applicationStore.getState().messageSlice.threadData;
          // var chatInputMessage = applicationStore.getState().messageSlice.chatInputMessage;
          if (threadData && threadData.id === payload.thread_id) {
            payload["source"] = "user";
            payload["is_user_message"] = true;
            payload["message_text"] = chatInputMessage;
            payload["id"] = payload["message_id"];
            dispatch(addMessage(payload));
            dispatch(setChatInputMessage(""));
          }
          if (message.payload.messaging_disabled !== undefined && message.payload.messaging_disabled !== null) {
            dispatch(setMessagingDisabled(message.payload.messaging_disabled));
          }
          dispatch(setShowAskToDraftModal({ show: false }));
          dispatch(setAwaitingResponse(true));
          break;
        case "chat/new-message-available":
          // // console.log("New message available: ", event.data);
          payload = message.payload;
          payload["source"] = "ai";
          payload["is_user_message"] = false;
          payload["text"] = payload["message_text"];
          payload["id"] = payload["message_id"];
          threadData = applicationStore.getState().messageSlice.threadData;

          var loading_message_type = payload["loading_message_type"];
          var lengthNewMessages = payload["new_messages"]?.length;
          var updateMessages = payload["update_messages"];
          if (updateMessages) {
            dispatch(updateThreadMessages(updateMessages));
            return;
          }
          if (loading_message_type && lengthNewMessages === 0 && !payload["generated_document_details"]) {
            dispatch(updateLoadingMessageType(loading_message_type));
            return;
          }
          if (threadData && threadData.id === payload.thread_id) {
            dispatch(addMessage(payload));
          }
          if (message.payload.messaging_disabled !== undefined && message.payload.messaging_disabled !== null) {
            dispatch(setMessagingDisabled(message.payload.messaging_disabled));
          } else if (message.payload.messaging_disabled === false) {
            dispatch(setMessagingDisabled(false));
            dispatch(setChangingDraftType(false));
          }
          if (message.payload?.information_messages) {
            message.payload.information_messages.forEach((element) => {
              dispatch(
                addMessage({
                  message_text: element,
                  message_type: "information_message",
                }),
              );
            });
          }
          dispatch(setAwaitingResponse(false));
          dispatch(setRegenerationHappening(false));
          break;
        case "chat/document-created":
          if (socketIdentityRef.current === "anonymous") {
            anonThreadIdsRef.current.add(message.payload.thread_id);
            rememberAnonymousThreadId(message.payload.thread_id);
          }
          // Event 101:
          trackAnalytics(
            EVENT_NAMES.RECEIVED_DRAFT,
            {
              [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
              [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
              [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
              [EVENT_PARAMETERS.THREAD_ID]: message.payload?.thread_id,
              [EVENT_PARAMETERS.UTM_CAMPAIGN]: queryParams.get("utm_campaign"),
              [EVENT_PARAMETERS.UTM_MEDIUM]: queryParams.get("utm_medium"),
              [EVENT_PARAMETERS.UTM_THREAD_ID]: queryParams.get("thread_id"),
              [EVENT_PARAMETERS.UTM_SOURCE]: queryParams.get("utm_source"),
            },
            userMetadata,
          );
          payload = message.payload;
          threadData = applicationStore.getState().messageSlice.threadData;
          if (threadData && threadData.id === payload.thread_id) {
            // dispatch(setDocument(payload));
            dispatch(addMessage(payload));
          }
          if (message.payload?.messaging_disabled !== undefined && message.payload?.messaging_disabled !== null) {
            dispatch(setMessagingDisabled(message.payload.messaging_disabled));
          }
          var metadata = applicationStore.getState().onboarding.userMetadata;
          if (Plan.from(metadata?.subscription_type).isPaid()) {
            threadData = applicationStore.getState().messageSlice.threadData;
            if (threadData && threadData.id && window.location.pathname.toLowerCase().search("shared") === -1) {
              dispatch(getAskThreadMessages({ threadId: threadData.id, dispatchSource: "regenerate-success" }));
            }
          }
          dispatch(setAwaitingResponse(false));
          dispatch(setRegenerationHappening(false));
          break;

        case "chat/document-deleted":
          // // console.log("Document deleted: ", event.data);
          break;
        case "ai-thread-title-available":
          // // console.log("AI thread title available: ", event.data);
          break;
        case "payment/link_expired":
          // Backend expired an old pending checkout because a new one was requested.
          // Signal CheckoutPage to show an inline expiry message if it has this payment_id open.
          dispatch(setPaymentLinkExpired({ paymentId: message.payload.payment_id }));
          break;
        case "payment/received":
          // Backend confirmed a successful charge. Signal CheckoutPage (or any open tab) to
          // navigate to the payment-status page so post-payment actions are triggered.
          dispatch(
            setPaymentReceived({
              paymentId: message.payload.payment_id,
              transactionType: message.payload.transaction_type,
              threadId: message.payload.thread_id,
            }),
          );
          break;
        default:
          // // console.log("Unknown message: ", event.data);
          break;
      }
      if (scroll) {
        dispatch(setScroll(false));
      }
    };
    // Helper functions for new message format handling
    const isNewFormat = (message) => {
      return message.payload && message.status_code !== undefined && message.payload.type !== undefined;
    };

    const handleNewFormatMessage = (message) => {
      // Handle new format messages
      if (message.status_code > 299) {
        // Handle error response
        handleNewFormatError(message);
        return true; // Indicates message was handled
      }

      // Route to appropriate handler based on type
      switch (message.payload.type) {
        case "pong":
          // Already handled above
          return true;
        default:
          // For now, let old handler process other types
          return false; // Indicates message should fall through to old handler
      }
    };

    const handleNewFormatError = (message) => {
      const { error, allow_regeneration, messaging_disabled, thread_limit_reached } = message.payload;

      // Map specific errors to user-friendly messages
      const errorMessages = {
        PaymentPendingException: "Please upgrade your subscription to access this feature.",
        ThreadNotFoundException: "This conversation was not found. Please refresh and try again.",
        AIInProgressException: "Please wait for the current response to complete.",
        AttachmentsStillProcessing: "Your files are still being processed. Please wait a moment.",
        FileNotReadableError: "Unable to read one or more files. Please try uploading again.",
        DocumentAlreadyCreatedException: "A document has already been created for this conversation.",
      };

      const userMessage = errorMessages[error] || error || "An error occurred. Please try again.";

      // Update state based on error type
      if (thread_limit_reached) {
        dispatch(setThreadLimitReached(true));
        dispatch(setShowThreadLimitModal(true));
      }

      if (messaging_disabled !== undefined) {
        dispatch(setMessagingDisabled(messaging_disabled));
      }

      if (allow_regeneration) {
        dispatch(setAllowRegeneration(true));
        // Add regeneration message to chat
        dispatch(
          addMessage({
            new_messages: [
              {
                message_type: "regenerate_ai_response",
                id: `regen-${Date.now()}`,
                created_at: new Date().toISOString(),
              },
            ],
          }),
        );
      }

      // Duplicate thread error → let the chat page handle redirect
      if (error === "Thread already exists with for the given GPT context id") {
        dispatch(setAwaitingResponse(false));
        dispatch(setAwaitingThreadCreationResponse(false));
        dispatch(setDuplicateThreadError(true));
        return;
      }

      // Show error toast
      toast.error(userMessage);

      // Log error for debugging
      ErrorLoggingV2({
        error_type: `WebSocket Error: ${message.action}`,
        error_message: error,
        payload: message,
      });

      // Reset loading states
      dispatch(setAwaitingResponse(false));
      dispatch(setRegenerationHappening(false));
      dispatch(setUnlockingDocument(false));
    };

    const handleLockedDocumentGenerated = (message) => {
      const { thread_id, doc_title, google_doc_id } = message.payload;

      dispatch(setUnlockingDocument(false));
      dispatch(updateLoadingMessageType("unlock_document_loading"));

      // Add locked document message
      dispatch(
        addMessage({
          thread_id,
          new_messages: [
            {
              message_type: "locked_document_generated",
              payload: {
                doc_title,
                google_doc_id,
              },
              id: `locked-doc-${Date.now()}`,
              created_at: new Date().toISOString(),
            },
          ],
        }),
      );
      dispatch(setAwaitingResponse(false));
    };

    const handleDocumentGenerated = (message) => {
      // Clear doc-gen timeout — document arrived via legacy path
      const docGenTriggerId = message.payload?.trigger_id;
      if (docGenTriggerId) {
        clearDocGenTimer(docGenTriggerId);
      }

      // const { thread_id, google_doc_id, is_locked, version } = message.payload;
      const thread_id = message.payload.thread_id;
      const docMessage = message.payload.new_messages?.find((msg) => msg.message_type === MESSAGE_TYPES.DOCUMENT);
      const doc_id = docMessage?.doc_id;
      const version = docMessage?.version;
      const is_user_message = docMessage?.is_user_uploaded;
      var is_locked = version === 1;
      if (is_user_message) {
        is_locked = false; // User-generated documents are never locked
      }
      if (version > 1) {
        const threadData = applicationStore.getState().messageSlice.threadData;
        threadData?.document_payments?.find((payment) => payment.doc_id === doc_id)
          ? (is_locked = false)
          : (is_locked = true);
      }

      dispatch(setUnlockingDocument(false));
      dispatch(setGenerateDocsFetchRequiredCount(1)); // Trigger doc library fetch

      // Track analytics
      trackAnalytics(EVENT_NAMES.RECEIVED_DRAFT, {
        [EVENT_PARAMETERS.THREAD_ID]: thread_id,
        [EVENT_PARAMETERS.DOCUMENT_ID]: doc_id,
        [EVENT_PARAMETERS.IS_LOCKED]: is_locked,
        [EVENT_PARAMETERS.IS_USER_DOCUMENT]: is_user_message,
        [EVENT_PARAMETERS.DOCUMENT_VERSION]: version,
      });

      // Track document edit completion for edit-fidelity measurement (version 2+ = edit, not new doc)
      if (version >= 2 && !is_user_message) {
        const threadData = applicationStore.getState().messageSlice.threadData;
        const previousVersion = threadData?.documents?.[doc_id]?.meta?.latest_version || version - 1;
        trackAnalytics(EVENT_NAMES.DOCUMENT_EDIT_COMPLETED, {
          [EVENT_PARAMETERS.THREAD_ID]: thread_id,
          [EVENT_PARAMETERS.DOCUMENT_ID]: doc_id,
          [EVENT_PARAMETERS.PREVIOUS_DOCUMENT_VERSION]: previousVersion,
          [EVENT_PARAMETERS.DOCUMENT_VERSION]: version,
          [EVENT_PARAMETERS.IS_DOCUMENT_EDIT]: true,
        });
      }
      trackDocumentEditedByUser({
        docMessage,
        threadId: thread_id,
        metadata: applicationStore.getState().onboarding.userMetadata,
      });

      // Add document message
      dispatch(addMessage(docMessage));

      // Start timer to check if messaging gets enabled
      // If not enabled within 60s, refetch list-messages to reset state
      startMessagingEnableTimer(thread_id);
    };

    const handleQuickActionButtonMessages = (message) => {
      const { thread_id, quick_action_buttons } = message.payload;

      // Get current state to check if this is for the active thread
      const currentState = applicationStore.getState();
      const currentThreadId = currentState.messageSlice.threadData?.id;

      // Only process if this message is for the currently active thread
      if (thread_id === currentThreadId) {
        const visibleButtons = (quick_action_buttons || []).filter((btn) => !btn.hide_from_user);
        // Add quick action buttons to the penultimate (second-to-last) message
        dispatch(
          addQuickActionButtonsToMessage({
            threadId: thread_id,
            quickActionButtons: visibleButtons,
            targetPosition: "penultimate",
          }),
        );

        // console.log(
        //   `Added ${quick_action_buttons.length} quick action buttons to penultimate message in thread ${thread_id}`
        // );
      } else {
        // console.log(`Ignoring quick action buttons for thread ${thread_id} (current thread: ${currentThreadId})`);
      }
    };

    const handleQuickActionsMessage = (message) => {
      const { thread_id } = message.payload;

      // Get current state to check if this is for the active thread
      const currentState = applicationStore.getState();
      const currentThreadId = currentState.messageSlice.threadData?.id;

      // Only process if this message is for the currently active thread
      if (thread_id === currentThreadId) {
        const visibleButtons = (message.payload?.buttons || []).filter((btn) => !btn.hide_from_user);
        // Add as a new message with buttons
        const quickActionsMessage = {
          id: `quick-actions-${Date.now()}`,
          is_user_message: false,
          created_at: new Date().toISOString(),
          message_type: "quick_actions",
          payload: { ...message.payload, buttons: visibleButtons },
        };

        // Use addMessage to add this as a new message
        dispatch(
          addMessage({
            thread_id,
            new_messages: [quickActionsMessage],
          }),
        );

        // console.log(`Added quick_actions message to thread ${thread_id}`);
      }
    };

    const originalOnMessage = receiveMessage;
    if (socketRef.current) {
      socketRef.current.onmessage = async (event) => {
        // Update the last pong received time whenever any message is received
        lastPongReceivedRef.current = Date.now();
        // Reset missed pongs counter - we received a response
        missedPongsCountRef.current = 0;

        let parsedMessage;
        try {
          parsedMessage = JSON.parse(event.data);

          // Check if this is the new message format
          if (isNewFormat(parsedMessage)) {
            // Handle new format message
            const wasHandled = handleNewFormatMessage(parsedMessage);
            if (wasHandled) {
              return; // Message was fully handled, no need to process further
            }
            // If not fully handled, continue with legacy processing
          }

          // Handle old format pong for backward compatibility
          if (parsedMessage.action === "pong" || (parsedMessage.payload && parsedMessage.payload.type === "pong")) {
            lastPongReceivedRef.current = Date.now();
            return; // No need to process pongs further
          }
        } catch {
          // Not a JSON message, skip
          if (originalOnMessage) return originalOnMessage(event);
          return;
        }
        if (parsedMessage && parsedMessage.trigger_ws_message_id) {
          const unacknowledged = { ...getUnacknowledgedMessages() };
          if (unacknowledged[parsedMessage.trigger_ws_message_id]) {
            if (parsedMessage.action === "chat/message-added") {
              try {
                unacknowledged[parsedMessage.trigger_ws_message_id].messageSent = true;
              } catch (e) { }
            } else {
              delete unacknowledged[parsedMessage.trigger_ws_message_id];
            }
            updateUnacknowledgedMessages(unacknowledged);
          }
        }
        if (originalOnMessage) return originalOnMessage(event);
      };
    }
    // Cleanup on component unmount
    return () => {
      // socket.close();
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  };

  // }, [url, accesstoken, retryCount, retryWS]);

  // Function to send a message through WebSocket
  const originalSendMessage = (message, scroll) => {
    // Reject add-message payloads that are missing a thread_id — these will
    // always fail backend validation and pollute the unsent-message retry queue.
    if (message?.action?.endsWith("/add-message") && !message?.payload?.thread_id) {
      ErrorLoggingV2(
        {
          error_type: "add-message sent without thread_id",
          error_message: JSON.stringify(message),
        },
        "ws_missing_thread_id",
      );
      return false;
    }

    var success = false;
    dispatch(setFailedToCreateThread(false));

    // Mark that user has attempted interaction
    dispatch(setUserInteractionAttempted(true));

    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(message));
      success = true;
    } else {
      // Show connection error modal since user is trying to interact
      dispatch(setShowConnectionErrorModal(true));
      ErrorLoggingV2(
        { error_type: `Socket connection not open/ active`, error_message: JSON.stringify(message) },
        "websocket_message_error",
      );
      dispatch(setAwaitingResponse(false));
      dispatch(setFailedToCreateThread(true));
      return;
    }
    dispatch(setAwaitingResponse(true));
    if (scroll) {
      dispatch(setScroll(true));
    }
    if (success) {
      return success;
    }
  };
  // Helper to get current unacknowledged messages from store
  const getUnacknowledgedMessages = () => {
    return applicationStore.getState().messageSlice.unacknowledgedMessages || {};
  };

  // Helper to update unacknowledged messages in store
  const updateUnacknowledgedMessages = (messages) => {
    dispatch(setUnacknowledgedMessages(messages));
  };

  const sendMessage = (message, scroll) => {
    if (message && message.trigger_ws_message_id) {
      const unacknowledged = { ...getUnacknowledgedMessages() };
      unacknowledged[message.trigger_ws_message_id] = {
        message,
        sentAt: Date.now(),
      };
      updateUnacknowledgedMessages(unacknowledged);
    }
    // A new add-message starts a fresh flow — abandon any pending retry from a prior trigger.
    if (message?.action === "chat/add-message") {
      cancelAttachmentRetry();
      dispatch(setTriggerRetryExhausted(false));
    }
    return originalSendMessage(message, scroll);
  };

  const createMessage = (_threadType, api, payload) => {
    let r = (Math.random() + 1).toString(36).substring(2, 8);
    // payload["type"] = _threadType;
    var data = { ...payload };

    // Attach timezone + geolocation context to every trigger-ai-response frame.
    if (api === "trigger-ai-response") {
      Object.assign(data, getTriggerGeoFields());
    }

    dispatch(addRequestIds(r));
    return {
      action: `chat/${api}`,
      payload: data,
      trigger_ws_message_id: r,
    };
  };

  // Fire a trigger held by the email-context send gate, once every email thread
  // it was waiting on has a terminal polled status. Runs on changes to the
  // polled map only — `attachmentsProcessingComplete` can't be used here because
  // it initialises to `true` (vacuously complete so Send isn't gated before
  // anything is attached), which would fire the trigger straight back into the
  // rejection it was held for. `failed` counts as terminal: the backend gate
  // clears too, and the turn should proceed on whatever context did extract.
  useEffect(() => {
    const deferred = deferredEmailTriggerRef.current;
    if (!deferred) return;
    if (!areEmailThreadsTerminal(deferred.emailThreadIds, emailAttachmentStatusByThreadId)) return;

    const activeThreadId = applicationStore.getState().messageSlice.threadData?.id;
    if (activeThreadId !== deferred.threadId) {
      // User navigated away mid-extraction — drop it. Revisiting the thread
      // re-triggers from history via deriveNeedsAiTrigger.
      deferredEmailTriggerRef.current = null;
      return;
    }
    // Keep the deferral pending while offline; the reconnect path re-triggers.
    if (!isConnectedRef.current) return;

    deferredEmailTriggerRef.current = null;
    const triggerAIMessage = createMessage("ask", "trigger-ai-response", {
      thread_id: deferred.threadId,
      last_message_id: deferred.lastMessageId,
      button_click_type: deferred.buttonClickType,
    });
    if (sendMessage(triggerAIMessage, true)) {
      dispatch(setLastTriggeredMessageId(deferred.lastMessageId));
      dispatch(setAwaitingResponse(true));
      dispatch(setMessagingDisabled(true));
      startStatusPolling(deferred.threadId);
    } else {
      console.warn("Failed to send deferred email-context trigger-ai-response");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailAttachmentStatusByThreadId]);

  // Retry a failed AI turn. Re-sends `trigger-ai-response` with `regenerate: true`
  // and the SAME user-message id the failed turn used — never the marker id, and
  // never a fresh `add-message` (the user turn is already stored server-side).
  const regenerateResponse = () => {
    const state = applicationStore.getState().messageSlice;
    // Prefer the id we remembered when the turn was triggered; fall back to
    // deriving it from history (e.g. after a reload). Returns null when there is
    // nothing safe to retry.
    const triggerAIPayload = buildRegeneratePayload({
      threadId: state.threadData?.id,
      lastTriggeredMessageId: state.lastTriggeredMessageId,
      messages: state.threadData?.messages,
      buttonClickType: state.buttonClickTypeForTrigger,
    });

    if (!triggerAIPayload) {
      console.warn("Cannot regenerate: missing thread id or last_message_id");
      return false;
    }

    const threadId = triggerAIPayload.thread_id;
    const lastMessageId = triggerAIPayload.last_message_id;
    const triggerAIMessage = createMessage("ask", "trigger-ai-response", triggerAIPayload);
    const success = sendMessage(triggerAIMessage, true);
    dispatch(setButtonClickTypeForTrigger(null));

    trackAnalytics(EVENT_NAMES.REGENERATE_CLICKED, {
      [EVENT_PARAMETERS.THREAD_ID]: threadId,
      [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.REGENERATE_RESPONSE,
      last_message_id: lastMessageId,
    });

    if (success) {
      // The retry is a fresh turn — resume awaiting/streaming + slow-response
      // monitoring exactly like a normal trigger.
      dispatch(setLastTriggeredMessageId(lastMessageId));
      dispatch(setRegenerationHappening(true));
      dispatch(setAwaitingResponse(true));
      dispatch(setMessagingDisabled(true));
      startStatusPolling(threadId);
    } else {
      console.warn("Failed to send regenerate trigger-ai-response");
    }
    return success;
  };

  const startPingInterval = () => {
    // Clear any existing interval
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }

    // Reset missed pongs counter when starting fresh
    missedPongsCountRef.current = 0;

    // Start a new ping interval
    pingIntervalRef.current = setInterval(() => {
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        // Increment missed pongs counter BEFORE sending ping
        // It will be reset to 0 when we receive any message (including pong)
        missedPongsCountRef.current += 1;

        // Check if we've missed too many pongs
        if (missedPongsCountRef.current >= MAX_MISSED_PONGS && !reconnectionAttemptInProgressRef.current) {
          dispatch(incrementReconnectionAttemptCount());
          const reconnectionCount = applicationStore.getState().messageSlice.reconnectionAttemptCount;

          console.warn(
            `Missed ${missedPongsCountRef.current} consecutive pongs. Reconnection attempt ${reconnectionCount}/5...`,
          );

          // Only show modal after 5 failed reconnection attempts (~75-90 seconds)
          if (reconnectionCount >= 5) {
            dispatch(setLastErrorMessage("Unable to establish connection. Please check your network."));
            dispatch(setShowConnectionErrorModal(true));
          } else {
            // Silently attempt reconnection without showing modal
          }

          // Attempt reconnection
          reconnectionAttemptInProgressRef.current = true;
          connectionStateRef.current = "disconnected";
          cleanupExistingConnection();

          // Fetch latest messages if on thread page
          const threadData = applicationStore.getState().messageSlice.threadData;
          if (threadData && threadData.id && window.location.pathname.toLowerCase().search("shared") === -1) {
            dispatch(getAskThreadMessages({ threadId: threadData.id, dispatchSource: "ws-pong-stale-reconnect" }));
          }

          setTimeout(() => {
            connectWebSocket();
            reconnectionAttemptInProgressRef.current = false;
          }, 1000);

          return; // Don't send ping, we're reconnecting
        }

        // Send a ping message to the server
        const pingMessage = {
          trigger_ws_message_id: randomString(10),
          action: "ping",
          payload: null, // Change from {} to null per new spec
        };
        try {
          socketRef.current.send(JSON.stringify(pingMessage));
        } catch (e) {
          console.warn("Failed to send ping", e);
        }
      }
    }, PING_INTERVAL); // Send ping every 15 seconds
  };

  const setupConnectionCheck = () => {
    // Clear any existing interval
    if (connectionCheckIntervalRef.current && socketRef?.current?.readyState !== WebSocket.CONNECTING) {
      clearInterval(connectionCheckIntervalRef.current);
    }

    connectionCheckIntervalRef.current = setInterval(() => {
      const currentTime = Date.now();
      const timeSinceLastPong = currentTime - lastPongReceivedRef.current;

      // Periodically fetch list messages if connection seems degraded
      // (every 3 ping intervals = ~45 seconds without response)
      if (timeSinceLastPong > PING_INTERVAL * 3) {
        if (isThreadPage()) {
          const threadData = applicationStore.getState().messageSlice.threadData;
          if (threadData && threadData.id) {
            if (listMessagesLastFetchedRef.current < Date.now() - LIST_MESSAGES_FETCH_INTERVAL) {
              listMessagesLastFetchedRef.current = Date.now();
              if (window.location.pathname.toLowerCase().search("shared") === -1) {
                dispatch(getAskThreadMessages({ threadId: threadData.id, dispatchSource: "ws-pong-fetchInterval" }));
              }
            }
          }
        }
      }

      // Backup reconnection check - if connection timeout exceeded and no reconnection in progress
      // This handles edge cases where ping interval might have stopped
      if (
        timeSinceLastPong > CONNECTION_TIMEOUT &&
        !reconnectionAttemptInProgressRef.current &&
        connectionStateRef.current === "connected"
      ) {
        dispatch(incrementReconnectionAttemptCount());
        const reconnectionCount = applicationStore.getState().messageSlice.reconnectionAttemptCount;

        console.warn(
          `Connection timeout exceeded (${Math.round(timeSinceLastPong / 1000)}s). Reconnection attempt ${reconnectionCount}/5...`,
        );

        // Only show modal after 5 failed reconnection attempts (~75-90 seconds)
        if (reconnectionCount >= 5) {
          dispatch(setLastErrorMessage("Unable to establish connection. Please check your network."));
          dispatch(setShowConnectionErrorModal(true));
        }

        // Attempt reconnection
        reconnectionAttemptInProgressRef.current = true;
        connectionStateRef.current = "disconnected";
        cleanupExistingConnection();

        // Fetch latest messages if on thread page
        const threadData = applicationStore.getState().messageSlice.threadData;
        if (threadData && threadData.id && window.location.pathname.toLowerCase().search("shared") === -1) {
          dispatch(getAskThreadMessages({ threadId: threadData.id, dispatchSource: "ws-backup-reconnect" }));
        }

        setTimeout(() => {
          connectWebSocket();
          reconnectionAttemptInProgressRef.current = false;
        }, 1000);
      }
    }, CONNECTION_CHECK_INTERVAL);
  };

  // Handle incoming messages (optional)

  return (
    <WebSocketContext.Provider
      value={{
        sendMessage,
        createMessage,
        regenerateResponse,
        socketRef,
        startStatusPolling,
        stopStatusPolling,
        cancelAttachmentRetry,
        connectionIdentity,
        syncPathname: setCurrentPathname,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

// Hook to use WebSocket context in any component
export const useWebSocketContext = () => useContext(WebSocketContext);

/**
 * WebSocketProvider mounts OUTSIDE the Router (App.js), so it can't call
 * useLocation itself. This bridge mounts INSIDE the Router and pushes
 * pathname changes into the provider so identity reconciliation re-runs on
 * navigation (the /try → /chat handoff is exactly where the anonymous socket
 * used to survive with the wrong identity).
 */
export const WebSocketLocationBridge = () => {
  const location = useLocation();
  const { syncPathname } = useWebSocketContext();

  useEffect(() => {
    syncPathname(location.pathname);
  }, [location.pathname, syncPathname]);

  return null;
};
